// src/lib/pharmacyFirst/pathways/aht/weight-selection.pathway.ts
//
// AHT Weight Management — Drug Selection Pathway
//
// Source of truth: AHT_Weight_Drug_Selection_Pathway__1_.docx (in project files).
// This file maps Stages 1–5 of that document into pathway config form.
//
// =============================================================================
// STAGE 1 OF 5 — PATIENT PROFILE (Q1, Q1a, Q2, Q2a, Q2b)
// Subsequent stages will be appended after Stage 1 review.
// =============================================================================
//
// Elimination model:
//   All seven drugs (tirzepatide, semaglutide, oral semaglutide, Foundayo, liraglutide, mysimba, orlistat) start
//   eligible. Each clinical question may remove zero or more drugs from the
//   eligible set. The DrugResolutionNode at the end applies clinical ranking
//   (tirzepatide → semaglutide → liraglutide → mysimba → orlistat) to whatever
//   remains. No pharmacist override.

import type {
  Pathway,
  ConditionRouteNode,
  DrugEliminationNode,
  DrugResolutionNode,
  NumericInputNode,
  ClinicianAssessmentNode,
  AttestationNode,
  GatewayProgressionNode,
  IOTFLookupNode,
  YesNoNode,
  MultiSelectNode,
  SingleSelectNode,
  FlagNode,
} from "../../types/pathway.types";
import type {
  AHTWeightInput,
  BiologicalSex,
  EthnicAdjustmentGroup,
} from "../../types/patient.types";
import { foundayoNeverRestartFor } from "./_shared/foundayo-never-restart";
import { confirmedBiologicalSex } from "../../engine/refinedFlowUi";

// -----------------------------------------------------------------------------
// Node IDs
// -----------------------------------------------------------------------------

// Stage 1
const N_Q1_AGE_ROUTE = "Q1_age_route";
// BUG6 / V2_E2: a transfer patient keeps only the medicine they are already taking in the eligible set.
const N_FDY_NEVER_RESTART = "Foundayo_never_restart_elim";
const N_TRANSFER_LOCK_ROUTE = "Transfer_lock_route";
const N_TRANSFER_LOCK_ELIM = "Transfer_lock_eliminate_other_drugs";
const N_Q1_AGE75_ELIM = "Q1_age75_eliminate_liraglutide_mysimba";

// Q1a (adolescent branch)
const N_Q1A_WEIGHT_PRESENT_ROUTE = "Q1a_weight_present_route";
const N_Q1A_WEIGHT_ENTRY = "Q1a_weight_entry";
const N_Q1A_WEIGHT_GATE = "Q1a_weight_above_60kg_gate";
const N_Q1A_IOTF_LOOKUP = "Q1a_iotf_lookup";
const N_Q1A_CONSENT = "Q1a_consent_assessment";
const N_Q1A_EATING_DISORDER = "Q1a_eating_disorder_screen";
const N_Q1A_SAFEGUARDING = "Q1a_safeguarding_concern";
const N_Q1A_PARENTAL = "Q1a_parental_involvement";
const N_Q1A_FACE_TO_FACE = "Q1a_face_to_face_attestation";
const N_Q1A_ADOLESCENT_ELIM = "Q1a_eliminate_non_liraglutide_drugs";

// Q2 BMI
const N_Q2_BMI_PRESENT_ROUTE = "Q2_bmi_present_route";
const N_Q2_BMI_ENTRY = "Q2_bmi_entry";
const N_Q2_BMI_BELOW27_ROUTE = "Q2_bmi_below_27_route";
const N_Q2_ORLISTAT_THRESHOLD_ELIM = "Q2_bmi_below_28_eliminate_orlistat";

// Q2 BMI <27 — established patient continuation sub-flow
// Reachable only when BMI <27 AND patient may already be on a weight management
// medicine. Per source document: "BMI <27 kg/m² → ALL drugs EXCLUDED (unless
// already established on the drug — document and continue with caution)."
const N_Q2_ESTABLISHED_CHECK = "Q2_bmi_below_27_established_check";
const N_Q2_ESTABLISHED_DRUG_PICK = "Q2_bmi_below_27_established_drug_pick";
const N_Q2_ESTABLISHED_FLOOR_ROUTE = "Q2_bmi_below_27_established_floor_route";
const N_Q2_CONT_TIRZ = "Q2_established_continuation_tirzepatide";
const N_Q2_CONT_SEMA = "Q2_established_continuation_semaglutide";
const N_Q2_CONT_LIRA = "Q2_established_continuation_liraglutide";
const N_Q2_CONT_MYSI = "Q2_established_continuation_mysimba";
const N_Q2_CONT_ORLI = "Q2_established_continuation_orlistat";
const N_Q2_CONT_FDY = "Q2_established_continuation_foundayo";
const N_Q2_CONT_OSEM = "Q2_established_continuation_oral_semaglutide";
// BUG5: a returning patient below the starting BMI is not asked 'established and tolerating'; restart criteria were met in the shared assessment.
const N_Q2_REINIT_FLAG = "Q2_returning_restart_criteria_flag";
const N_Q2_REINIT_LOCK_ROUTE = "Q2_returning_lock_to_returning_drug";

// Q2a comorbidities
const N_Q2A_COMORBIDITIES = "Q2a_comorbidities_checklist";
const N_Q2A_LIRAGLUTIDE_QUALIFYING_ELIM = "Q2a_liraglutide_qualifying_comorbidity_check";
const N_Q2A_T2DM_ROUTE = "Q2a_t2dm_route";
const N_Q2A_NO_COMORBIDITY_ROUTE = "Q2a_no_comorbidity_route";
const N_Q2A_NO_COMORBIDITY_ETHNIC_ELIM = "Q2a_no_comorbidity_ethnic_adj_elim";

// Q2b HbA1c
const N_Q2B_HBA1C_ENTRY = "Q2b_hba1c_entry";
const N_Q2B_HBA1C_ROUTE = "Q2b_hba1c_route";
const N_Q2B_HBA1C_MID_FLAG = "Q2b_hba1c_mid_gp_review_flag";
const N_Q2B_HBA1C_HIGH_ELIM = "Q2b_hba1c_high_eliminate_glp1_mysimba";
const N_Q2B_HBA1C_HIGH_ORLISTAT_CHECK = "Q2b_hba1c_high_orlistat_remaining_check";
const N_Q2B_HBA1C_RECENCY = "Q2b_hba1c_within_6_months";
const N_Q2B_HBA1C_STALE_ELIM = "Q2b_hba1c_not_recent_eliminate_foundayo";

// Stage 2 — Contraindication screening
const N_Q3_THYROID = "Q3_thyroid_cancer_men2";
const N_Q4_PANCREATITIS = "Q4_pancreatitis_history";
const N_Q5_T1DM_T3C = "Q5_type_1_or_type_3c_diabetes";
const N_Q6_SULPHONYLUREAS_INSULIN = "Q6_sulphonylureas_or_insulin";
const N_Q7_RENAL_HEPATIC = "Q7_severe_renal_or_hepatic_impairment";
const N_Q7_SEVERE_RENAL_ELIM      = "Q7_severe_renal_elimination";
const N_Q7_LIRAGLUTIDE_RENAL_ELIM = "Q7_liraglutide_renal_threshold_elimination";
const N_Q7_ORAL_SEMA_EGFR_ELIM = "Q7_oral_sema_egfr_below_30_elim";
const N_Q7_MODERATE_RENAL_FLAG    = "Q7_moderate_renal_mysimba_flag";
const N_Q7_HEPATIC_ELIM           = "Q7_severe_hepatic_elimination";
const N_Q7_MOD_HEPATIC_ELIM       = "Q7_moderate_hepatic_mysimba_elimination";
const N_Q8_SEIZURES_CNS = "Q8_seizures_or_cns_tumour";
const N_Q9A_EATING_DISORDER         = "Q9a_eating_disorder";
const N_Q9B_BIPOLAR                 = "Q9b_bipolar";
const N_Q9_EATING_DISORDER_UPLOAD   = "Q9_eating_disorder_document_upload";
const N_Q9_EATING_DISORDER_ATTEST   = "Q9_eating_disorder_sign_off_attestation";
const N_Q9_BIPOLAR_UPLOAD           = "Q9_bipolar_document_upload";
const N_Q9_BIPOLAR_ATTEST           = "Q9_bipolar_sign_off_attestation";
const N_Q9C_FDY_GUARD               = "Q9c_foundayo_disordered_eating_guard";
const N_Q9C_FDY_SCREEN              = "Q9c_foundayo_disordered_eating_screen";
const N_Q9C_FDY_ELIM                = "Q9c_foundayo_disordered_eating_elim";
// N_Q10_OPIOID_USE and N_Q11_MAOIS removed — Q10/Q11 replaced by Group B autoAdvance elim nodes.
const N_Q12_UNCONTROLLED_BP = "Q12_blood_pressure_controlled";
const N_Q12_BP_DECISION      = "Q12_bp_controlled_decision";
const N_Q12_BP_ELIM          = "Q12_bp_mysimba_elimination";
const N_Q13_SUICIDAL_IDEATION = "Q13_active_suicidal_ideation";
const N_Q14_SEVERE_GI = "Q14_severe_gi_or_gastroparesis";
const N_Q15_PREGNANCY = "Q15_pregnancy_breastfeeding_planning";
const N_Q15_SEX_GATE = "Q15_sex_gate";
const N_Q16_CHOLESTASIS = "Q16_cholestasis_or_malabsorption";
const N_Q17_CICLOSPORIN = "Q17_ciclosporin";
const N_Q18_DIABETIC_RETINOPATHY = "Q18_diabetic_retinopathy";
const N_Q19_NYHA_IV_HF = "Q19_nyha_class_iv_heart_failure";
const N_STAGE2_EXIT_GUARD = "Stage2_exit_eligible_set_guard";

// Group A — GLP-1 contraindications (replaces individual Q3, Q4, Q5 nodes)
const N_Q3_GROUP_A_ROUTE         = "Q3_glp1_contraindications_route";
const N_Q3_THYROID_ELIM          = "Q3_thyroid_autoadvance_elim";
const N_Q4_PANCREATITIS_ELIM     = "Q4_pancreatitis_autoadvance_elim";
const N_Q5_T1DM_ELIM             = "Q5_t1dm_autoadvance_elim";

// Group B — Mysimba CNS contraindications (replaces individual Q8, Q10, Q11 nodes)
const N_Q8_GROUP_B_ROUTE         = "Q8_mysimba_cns_contraindications_route";
const N_Q8_SEIZURES_ELIM         = "Q8_seizures_autoadvance_elim";
const N_Q10_OPIOID_ELIM          = "Q10_opioid_autoadvance_elim";
const N_Q11_MAOIS_ELIM           = "Q11_maois_autoadvance_elim";

// Group C — GI and malabsorption (replaces individual Q14, Q16 nodes)
const N_Q14_GROUP_C_ROUTE        = "Q14_gi_reproductive_route";
const N_Q14_GI_ELIM              = "Q14_gi_autoadvance_elim";
const N_Q14_GI_TO_Q16_ROUTE      = "Q14_gi_to_q16_route";
// N_Q16_CHOLESTASIS reused for q16CholestasisElim to preserve Q15's answerRouting.no

// Group D — Cardiac, retinal, drug interactions (replaces individual Q17, Q18, Q19 nodes)
// N_Q17_CICLOSPORIN reused for q17GroupDChecklist (entry point preserved)
const N_Q17_GROUP_D_ROUTE        = "Q17_cardiac_retinal_drug_route";
const N_Q17_CICLOSPORIN_ELIM     = "Q17_ciclosporin_autoadvance_elim";
const N_Q18_RETINOPATHY_ELIM     = "Q18_retinopathy_autoadvance_elim";
const N_Q19_NYHA_ELIM            = "Q19_nyha_autoadvance_elim";
const N_Q17_TO_Q18_ROUTE         = "Q17_to_q18_route";
const N_Q17_TO_Q19_ROUTE         = "Q17_to_q19_route";
const N_Q18_TO_Q19_ROUTE         = "Q18_to_q19_route";

// Stage 3 — Interaction screening
const N_Q20A_CYP2B6_INHIBITORS = "Q20_interactions_checklist";
const N_Q20_CYP2B6_INHIBITOR_PLAN   = "Q20b_cyp2b6_inhibitor_plan";
const N_Q20_CYP2B6_INDUCER_PLAN      = "Q20c_cyp2b6_inducer_plan";
const N_Q20_LEVOTHYROXINE_PLAN           = "Q20d_levothyroxine_plan";
const N_Q20_WARFARIN_PLAN     = "Q20e_warfarin_plan";
const N_Q20_MANAGEMENT_ROUTE    = "Q20_management_plan_route";
const N_Q20_ANTIEPILEPTICS_PLAN = "Q20f_tamoxifen_plan";
const N_Q20_TAMOXIFEN_PLAN      = "Q20f_tamoxifen_mysimba";
// Foundayo-only medicines check (PGD v2.1 Exclusion criteria > Medicines, Interactions). Runs after the shared Q20.
const N_Q20F_GUARD              = "Q20F_foundayo_guard";
const N_Q20F_CHECKLIST          = "Q20F_foundayo_medicines_checklist";
const N_Q20F_EXCLUDE_ELIM       = "Q20F_foundayo_excluded_medicines_elim";
const N_Q20F_TOPOTECAN_PLAN     = "Q20F_foundayo_oral_topotecan_plan";
const N_Q20F_STATIN_PLAN        = "Q20F_foundayo_statin_plan";
const N_Q20F_CAP_PLAN           = "Q20F_foundayo_dose_cap_plan";
const N_Q20F_INDUCER_PLAN       = "Q20F_foundayo_moderate_inducer_plan";
const N_STAGE3_EXIT_GUARD = "Stage3_exit_eligible_set_guard";

// Stage 4 — Patient preferences
const N_QP1_GUARD = "QP1_guard_injectables_remain";
const N_QP1_INJECTION_WILLING = "QP1_injection_willing";
const N_QP2_GUARD = "QP2_guard_both_injectable_types_remain";
const N_QP2_WEEKLY_VS_DAILY = "QP2_weekly_vs_daily_injection_preference";
const N_QP2_WEEKLY_ELIM = "QP2_weekly_preference_elim";
const N_QP2_DAILY_ELIM  = "QP2_daily_preference_elim";
const N_QP3_GUARD = "QP3_guard_both_orals_remain";
const N_QP3_ORAL_PREFERENCE = "QP3_oral_only_preference";
const N_QP3_ELIM_ORLISTAT = "QP3_favours_orlistat_elim";
const N_QP3_ELIM_MYSIMBA  = "QP3_favours_mysimba_elim";
const N_STAGE4_EXIT_GUARD = "Stage4_exit_eligible_set_guard";
const N_COMORBIDITY_MODIFIER_NAFLD_GATE   = "comorbidity_modifier_nafld_gate";
const N_COMORBIDITY_MODIFIER_NAFLD_UPLOAD = "comorbidity_modifier_nafld_mysimba_upload";
const N_COMORBIDITY_MODIFIER_NAFLD_ATTEST = "comorbidity_modifier_nafld_mysimba_attestation";
const N_COMORBIDITY_MODIFIER_ANNOTATIONS  = "comorbidity_modifier_ranking_annotations";
const N_COMORBIDITY_CVD_FLAG              = "comorbidity_cvd_flag_node";
const N_COMORBIDITY_NAFLD_ANNOT           = "comorbidity_nafld_annot_check";
const N_COMORBIDITY_NAFLD_FLAG            = "comorbidity_nafld_flag_node";
const N_COMORBIDITY_PCOS_ANNOT            = "comorbidity_pcos_annot_check";
const N_COMORBIDITY_PCOS_FLAG             = "comorbidity_pcos_flag_node";
const N_COMORBIDITY_OA_ANNOT              = "comorbidity_oa_annot_check";
const N_COMORBIDITY_OA_FLAG               = "comorbidity_oa_flag_node";

// Stage 5 — Drug resolution and confirmation
const N_DRUG_RESOLUTION = "drug_resolution_final";
const N_RESOLUTION_CONFIRMATION = "resolution_confirmation_attestation";

// -----------------------------------------------------------------------------
// Outcome IDs
// -----------------------------------------------------------------------------

const O_REFER_UNDER12 = "OUT_REFER_UNDER12";
const O_REFER_OVER84 = "OUT_REFER_OVER84"; // age ≥85 — outside PGD range for all medicines
const O_REFER_GP = "OUT_REFER_GP";
const O_REFER_BMI_BELOW27 = "OUT_REFER_BMI_BELOW27"; // BMI <27 and either not established OR below drug-specific discontinuation floor
const O_REFER_ADOLESCENT_FAILED = "OUT_REFER_ADOLESCENT_FAILED";
const O_REFER_MENTAL_HEALTH_URGENT = "OUT_REFER_MENTAL_HEALTH_URGENT";
const O_REFER_PREGNANCY = "OUT_REFER_PREGNANCY";
const O_BLOCKED_PENDING_IOTF = "OUT_BLOCKED_PENDING_IOTF";
const O_SUPPLY_SELECTED = "OUT_SUPPLY_SELECTED";

// -----------------------------------------------------------------------------
// Clinical decision helpers
//
// Per project rule (§7 of project instructions): "Any condition more complex
// than two boolean checks becomes a named function in /pathway/decisions/ with
// unit tests." These are exported for unit testing in the test phase. They
// should move to src/lib/pharmacyFirst/pathway/decisions/aht-weight.decisions.ts
// when the test suite is built (phase 11).
// -----------------------------------------------------------------------------

export {
  AHT_SENSITIVE_ATTRIBUTE_CONSENT_BASIS,
  AHT_BIOLOGICAL_SEX_PURPOSE,
  AHT_ETHNIC_ADJUSTMENT_PURPOSE,
} from "./_shared/ahtDemographicBanner";

const ETHNIC_ADJUSTMENT_GROUPS = [
  "south_asian",
  "chinese",
  "other_asian",
  "middle_eastern",
  "black_african",
  "african_caribbean",
] as const;

function normalizePatientRecordValue(value: string | null | undefined): string {
  return (value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/\s+/g, " ");
}

export function mapGenderToBiologicalSex(
  gender: string | null | undefined
): BiologicalSex | undefined {
  const normalized = normalizePatientRecordValue(gender);
  if (normalized.startsWith("m")) return "male";
  if (normalized.startsWith("f")) return "female";
  return undefined;
}

// TODO: AHT clinical sign-off — working mapping, especially mixed categories.
export function mapEthnicityToAdjustmentGroup(
  ethnicity: string | null | undefined
): EthnicAdjustmentGroup | undefined {
  const normalized = normalizePatientRecordValue(ethnicity);
  if (!normalized) return undefined;
  if (
    normalized.includes("not specified") ||
    normalized.includes("not stated") ||
    normalized.includes("not known") ||
    normalized.includes("unknown")
  ) {
    return undefined;
  }
  if (normalized.includes("mixed")) return undefined;
  if (normalized.includes("any other ethnic group")) return undefined;
  if (normalized.includes("chinese")) return "chinese";
  if (normalized.includes("indian") || normalized.includes("pakistani") || normalized.includes("bangladeshi")) {
    return "south_asian";
  }
  if (normalized.includes("any other asian")) return "other_asian";
  if (normalized.includes("black") && normalized.includes("caribbean")) {
    return "african_caribbean";
  }
  if (normalized.includes("black") && (normalized.includes("african") || normalized.includes("any other black"))) {
    return "black_african";
  }
  if (normalized.includes("white") && (normalized.includes("british") || normalized.includes("irish") || normalized.includes("any other"))) {
    return "other";
  }
  return undefined;
}

const INJECTABLE_DRUG_IDS = ["tirzepatide", "semaglutide", "liraglutide"] as const;
const WEEKLY_INJECTABLE_IDS = ["tirzepatide", "semaglutide"] as const;
const DAILY_INJECTABLE_IDS = ["liraglutide"] as const;
const ORAL_DRUG_IDS = ["mysimba", "orlistat"] as const;

const FAVOURS_ORLISTAT_FACTOR_IDS = [
  "history_depression_mood_disorder",
  "avoid_cns_active_medication",
  "concern_insomnia_sleep",
  "prefer_established_efficacy_data",
] as const;

const FAVOURS_MYSIMBA_FACTOR_IDS = [
  "concern_gi_side_effects",
  "low_fat_diet_difficulty",
  "concern_vitamin_deficiency",
  "concurrent_dietary_supplements_malabsorption_risk",
] as const;

// Patient ethnicity arrives either as a plain value or as a sensitive-attribute
// wrapper, and as an ethnicity label or an adjustment-group id.
export function getPatientEthnicAdjustmentGroup(
  patient: unknown
): string | undefined {
  const raw = (patient as any)?.ethnicAdjustmentGroup;
  const value = typeof raw === "object" && raw !== null ? raw.value : raw;
  return typeof value === "string" && value ? value : undefined;
}

// Accepts either an adjustment-group id or a patient-record ethnicity label.
export function isEthnicAdjustmentGroup(
  group: string | null | undefined
): boolean {
  if (!group) return false;
  const groups = ETHNIC_ADJUSTMENT_GROUPS as readonly string[];
  if (groups.includes(group)) return true;
  const mapped = mapEthnicityToAdjustmentGroup(group);
  return !!mapped && groups.includes(mapped);
}

export function hasAnyInjectable(eligibleDrugIds: readonly string[]): boolean {
  return eligibleDrugIds.some((id) =>
    (INJECTABLE_DRUG_IDS as readonly string[]).includes(id)
  );
}

export function hasWeeklyInjectable(eligibleDrugIds: readonly string[]): boolean {
  return eligibleDrugIds.some((id) =>
    (WEEKLY_INJECTABLE_IDS as readonly string[]).includes(id)
  );
}

export function hasDailyInjectable(eligibleDrugIds: readonly string[]): boolean {
  return eligibleDrugIds.some((id) =>
    (DAILY_INJECTABLE_IDS as readonly string[]).includes(id)
  );
}

export function hasBothOrals(eligibleDrugIds: readonly string[]): boolean {
  return (ORAL_DRUG_IDS as readonly string[]).every((id) =>
    eligibleDrugIds.includes(id)
  );
}

export function hasFavoursOrlistatFactor(
  selectedFactorIds: readonly string[]
): boolean {
  return selectedFactorIds.some((id) =>
    (FAVOURS_ORLISTAT_FACTOR_IDS as readonly string[]).includes(id)
  );
}

export function hasFavoursMysimbaFactor(
  selectedFactorIds: readonly string[]
): boolean {
  return selectedFactorIds.some((id) =>
    (FAVOURS_MYSIMBA_FACTOR_IDS as readonly string[]).includes(id)
  );
}

/**
 * Returns true if the BMI + ethnic adjustment combination puts the patient
 * in the "treat as BMI ≥30" band for tirzepatide/semaglutide/liraglutide.
 * Mysimba and orlistat thresholds are not adjusted by this rule.
 */
export function isEthnicAdjustedTo30(
  bmi: number,
  ethnicAdjustmentGroup: string | null | undefined
): boolean {
  return (
    bmi >= 27.5 &&
    bmi < 30 &&
    isEthnicAdjustmentGroup(ethnicAdjustmentGroup)
  );
}


// -----------------------------------------------------------------------------
// Stage 1 nodes
// -----------------------------------------------------------------------------

const TRANSFER_DRUG_KEY = "aht-sel-transfer-drug";

const foundayoNeverRestartElim: DrugEliminationNode = {
  id: N_FDY_NEVER_RESTART,
  type: "drug_elimination",
  text: "Foundayo: confirmed acute pancreatitis at an earlier review.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Foundayo-never-restart-confirmed-pancreatitis",
      drugIds: ["foundayo"],
      reason:
        "Acute pancreatitis was confirmed at an earlier Foundayo review. The PGD says do not restart. Refer to the GP.",
      trigger: {
        kind: "condition" as const,
        evaluate: (ctx: { patient: { patientId?: string } }) => foundayoNeverRestartFor(ctx.patient.patientId),
      },
    },
  ],
  eliminateOnNo: [],
  next: N_TRANSFER_LOCK_ROUTE,
  noDrugsRemaining: N_TRANSFER_LOCK_ROUTE,
};

const transferLockRoute: ConditionRouteNode = {
  id: N_TRANSFER_LOCK_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.context["aht-sel-consultation-type"] === "transfer",
      then: N_TRANSFER_LOCK_ELIM,
    },
  ],
  default: N_Q1_AGE_ROUTE,
};

// One rule per drug. With no recorded transfer medicine every drug is removed, so the patient is referred rather than ranked.
const transferLockElim: DrugEliminationNode = {
  id: N_TRANSFER_LOCK_ELIM,
  type: "drug_elimination",
  text: "Transfer: only the medicine the patient is already taking stays eligible.",
  autoAdvance: true,
  eliminateOnYes: [
    "tirzepatide",
    "semaglutide",
    "oral_semaglutide",
    "foundayo",
    "liraglutide",
    "mysimba",
    "orlistat",
  ].map((drugId) => ({
    id: `Transfer-lock-eliminates-${drugId}`,
    drugIds: [drugId],
    reason: `Transfer: the patient is established on a different medicine, so ${drugId} is not offered.`,
    trigger: {
      kind: "condition" as const,
      evaluate: (ctx: { context: Record<string, unknown> }) => ctx.context[TRANSFER_DRUG_KEY] !== drugId,
    },
  })),
  eliminateOnNo: [],
  next: N_Q1_AGE_ROUTE,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q1AgeRoute: ConditionRouteNode = {
  id: N_Q1_AGE_ROUTE,
  type: "condition_route",
  conditions: [
    {
      when: { patientAgeUnder: 12 },
      then: { outcomeId: O_REFER_UNDER12 },
    },
    {
      when: { patientAgeAtLeast: 12, patientAgeUnder: 18 },
      then: N_Q1A_WEIGHT_PRESENT_ROUTE,
    },
    {
      when: { patientAgeAtLeast: 18, patientAgeUnder: 75 },
      then: N_Q2_BMI_PRESENT_ROUTE,
    },
    {
      when: { patientAgeAtLeast: 75, patientAgeUnder: 85 },
      then: N_Q1_AGE75_ELIM,
    },
    {
      when: { patientAgeAtLeast: 85 },
      then: { outcomeId: O_REFER_OVER84 },
    },
  ],
  default: { outcomeId: O_REFER_GP }, // Defensive fallback; age branches above should cover every patient.
};

const q1Age75Elim: DrugEliminationNode = {
  id: N_Q1_AGE75_ELIM,
  type: "drug_elimination",
  text:
    "Patient is 75 years or over: liraglutide (≥75 not recommended per PGD) and mysimba (≥75 EXCLUDED per PGD) are eliminated. Continuing with tirzepatide, semaglutide, and orlistat only.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q1-age-75-or-over-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason: "Patient is 75 years or over: liraglutide (≥75 not recommended per PGD) and mysimba (≥75 EXCLUDED per PGD) are eliminated. Continuing with tirzepatide, semaglutide, and orlistat only.",
      trigger: { kind: "always" },
    },
    {
      id: "Q1-age-75-or-over-excludes-mysimba",
      drugIds: ["mysimba"],
      reason: "Patient is 75 years or over: liraglutide (≥75 not recommended per PGD) and mysimba (≥75 EXCLUDED per PGD) are eliminated. Continuing with tirzepatide, semaglutide, and orlistat only.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q2_BMI_PRESENT_ROUTE,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

// -----------------------------------------------------------------------------
// Q1a — Adolescent branch (12–17, liraglutide only)
// -----------------------------------------------------------------------------

// The confirm screens below are kept as the fallback for when the value is not already in context.
const q1aWeightPresentRoute: ConditionRouteNode = {
  id: N_Q1A_WEIGHT_PRESENT_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) =>
        typeof ctx.context.weightKg === "number" &&
        Number.isFinite(ctx.context.weightKg) &&
        ctx.context.weightKg > 0,
      then: N_Q1A_WEIGHT_GATE,
    },
  ],
  default: N_Q1A_WEIGHT_ENTRY,
};

const q1aWeightEntry: GatewayProgressionNode = {
  id: N_Q1A_WEIGHT_ENTRY,
  type: "gateway_progression",
  heading: "Q1a \u2014 Confirm patient weight",
  body: "Weight should already be recorded from the BMI vital recorded in the shared assessment.\n\nIf weight has not been recorded or needs updating:\nActions panel \u2192 Add Vital \u2192 BMI\n\nLiraglutide eligibility in adolescents requires body weight above 60 kg. Once confirmed, tap Continue.",
  triggersPayment: false,
  next: N_Q1A_WEIGHT_GATE,
};

const q1aWeightGate: ConditionRouteNode = {
  id: N_Q1A_WEIGHT_GATE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => Number(ctx.context.weightKg) <= 60,
      then: { outcomeId: O_REFER_ADOLESCENT_FAILED },
    },
    {
      evaluate: (ctx) => Number(ctx.context.weightKg) > 60,
      then: N_Q1A_IOTF_LOOKUP,
    },
  ],
  default: { outcomeId: O_REFER_ADOLESCENT_FAILED }, // Defensive fallback; weight branches above are exhaustive.
};

const q1aIotfLookup: IOTFLookupNode = {
  id: N_Q1A_IOTF_LOOKUP,
  type: "iotf_lookup",
  iotfTableId: "iotf-cutoffs-v1", // Currently pending_data until AHT supplies Appendix A.
  meetsThreshold: N_Q1A_CONSENT,
  belowThreshold: { outcomeId: O_REFER_ADOLESCENT_FAILED },
  pendingDataOutcome: { outcomeId: O_BLOCKED_PENDING_IOTF },
  sexNotResolvable: { outcomeId: O_REFER_ADOLESCENT_FAILED },
};

const q1aConsent: ClinicianAssessmentNode = {
  id: N_Q1A_CONSENT,
  type: "clinician_assessment",
  text:
    "Q1a — Consent assessment: Gillick competence (ages 12–15) OR capacity to consent (ages 16–17)",
  notesPrompt:
    "Record the basis for confirming Gillick competence or capacity to consent. Include factors considered (understanding, retention, weighing, communication).",
  conclusions: {
    optionA: {
      label: "Confirmed — competence/capacity present",
      next: N_Q1A_EATING_DISORDER,
    },
    optionB: {
      label: "Not confirmed — refer",
      next: { outcomeId: O_REFER_ADOLESCENT_FAILED },
    },
  },
};

const q1aEatingDisorder: YesNoNode = {
  id: N_Q1A_EATING_DISORDER,
  type: "yes_no",
  text:
    "Q1a — Does the patient have a suspected or confirmed eating disorder (e.g. anorexia nervosa, bulimia nervosa, ARFID)?",
  yes: { outcomeId: O_REFER_ADOLESCENT_FAILED },
  no: N_Q1A_SAFEGUARDING,
};

const q1aSafeguarding: YesNoNode = {
  id: N_Q1A_SAFEGUARDING,
  type: "yes_no",
  text:
    "Q1a — Is there an active or unresolved safeguarding concern for this patient?",
  yes: { outcomeId: O_REFER_ADOLESCENT_FAILED },
  no: N_Q1A_PARENTAL,
};

const q1aParental: ConditionRouteNode = {
  id: N_Q1A_PARENTAL,
  type: "condition_route",
  conditions: [
    {
      // Parental involvement already captured in shared assessment —
      // read stored value and route silently. Audit flag preserved from
      // shared assessment record. No re-ask.
      evaluate: (ctx) => true,
      then: N_Q1A_FACE_TO_FACE,
    },
  ],
  default: N_Q1A_FACE_TO_FACE,
};

const q1aFaceToFace: AttestationNode = {
  id: N_Q1A_FACE_TO_FACE,
  type: "attestation",
  text:
    "Q1a — Face-to-face consultation attestation (required for adolescent initial supply)",
  attestationStatement:
    "I attest that this consultation is being conducted face-to-face at a registered GPhC/CQC premises. Remote initial supply of liraglutide to patients under 18 is not permitted.",
  conclusions: {
    optionA: {
      label: "Attested — face-to-face consultation confirmed",
      next: N_Q1A_ADOLESCENT_ELIM,
    },
    optionB: {
      label: "Cannot attest — refer",
      next: { outcomeId: O_REFER_ADOLESCENT_FAILED },
    },
  },
};

const q1aAdolescentElim: DrugEliminationNode = {
  id: N_Q1A_ADOLESCENT_ELIM,
  type: "drug_elimination",
  text:
    "Adolescent eligibility confirmed: liraglutide is the only weight management medicine licensed for ages 12–17 under this PGD. Tirzepatide, semaglutide, mysimba, and orlistat are eliminated.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q1a-adolescent-excludes-tirzepatide-not-licensed-under-18",
      drugIds: ["tirzepatide"],
      reason: "Adolescent eligibility confirmed: liraglutide is the only weight management medicine licensed for ages 12–17 under this PGD. Tirzepatide, semaglutide, mysimba, and orlistat are eliminated.",
      trigger: { kind: "always" },
    },
    {
      id: "Q1a-adolescent-excludes-semaglutide-not-licensed-under-18",
      drugIds: ["semaglutide", "oral_semaglutide", "foundayo"],
      reason: "Adolescent eligibility confirmed: liraglutide is the only weight management medicine licensed for ages 12–17 under this PGD. Tirzepatide, semaglutide, mysimba, and orlistat are eliminated.",
      trigger: { kind: "always" },
    },
    {
      id: "Q1a-adolescent-excludes-mysimba-not-licensed-under-18",
      drugIds: ["mysimba"],
      reason: "Adolescent eligibility confirmed: liraglutide is the only weight management medicine licensed for ages 12–17 under this PGD. Tirzepatide, semaglutide, mysimba, and orlistat are eliminated.",
      trigger: { kind: "always" },
    },
    {
      id: "Q1a-adolescent-excludes-orlistat-not-licensed-under-18",
      drugIds: ["orlistat"],
      reason: "Adolescent eligibility confirmed: liraglutide is the only weight management medicine licensed for ages 12–17 under this PGD. Tirzepatide, semaglutide, mysimba, and orlistat are eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q3_THYROID,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

// -----------------------------------------------------------------------------
// Q2 — Adult BMI thresholds
// -----------------------------------------------------------------------------

// The confirm screen below is kept as the fallback for when BMI is not already in context.
const q2BmiPresentRoute: ConditionRouteNode = {
  id: N_Q2_BMI_PRESENT_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) =>
        typeof ctx.context.bmi === "number" &&
        Number.isFinite(ctx.context.bmi) &&
        ctx.context.bmi > 0,
      then: N_Q2_BMI_BELOW27_ROUTE,
    },
  ],
  default: N_Q2_BMI_ENTRY,
};

const q2BmiEntry: GatewayProgressionNode = {
  id: N_Q2_BMI_ENTRY,
  type: "gateway_progression",
  heading: "Q2 \u2014 Confirm patient BMI",
  body: "BMI should already be recorded from the shared assessment. Confirm the BMI value in the vitals panel is current before continuing.\n\nIf BMI has not been recorded or needs updating:\nActions panel \u2192 Add Vital \u2192 BMI\n\nOnce confirmed, tap Continue.",
  triggersPayment: false,
  next: N_Q2_BMI_BELOW27_ROUTE,
};

// The medicine a returning or transfer patient is continuing, taken from the earlier pathways. Undefined for new patients.
function lockedReturningOrTransferDrug(context: Record<string, unknown>): string | undefined {
  const type = context["aht-sel-consultation-type"];
  const drug =
    type === "transfer" ? context[TRANSFER_DRUG_KEY] : type === "reinitiation" ? context["aht-sel-reinitiation-drug"] : undefined;
  return typeof drug === "string" ? drug : undefined;
}

const q2BmiBelow27Route: ConditionRouteNode = {
  id: N_Q2_BMI_BELOW27_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => {
        const drug = lockedReturningOrTransferDrug(ctx.context);
        return drug !== undefined && Number(ctx.context.bmi) < (drug === "orlistat" ? 28 : 27);
      },
      then: N_Q2_REINIT_FLAG,
    },
    {
      evaluate: (ctx) => Number(ctx.context.bmi) < 27,
      then: N_Q2_ESTABLISHED_CHECK,
    },
    {
      evaluate: (ctx) => Number(ctx.context.bmi) >= 27,
      then: N_Q2_ORLISTAT_THRESHOLD_ELIM,
    },
  ],
  default: { outcomeId: O_REFER_GP }, // Defensive fallback; BMI branches above are exhaustive after BMI capture.
};

const q2OrlistatThresholdElim: DrugEliminationNode = {
  id: N_Q2_ORLISTAT_THRESHOLD_ELIM,
  type: "drug_elimination",
  text:
    "Orlistat requires BMI ≥28. If BMI is in the 27.0–27.9 band, orlistat is eliminated.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-bmi-below-28-excludes-orlistat",
      drugIds: ["orlistat"],
      reason: "Orlistat requires BMI ≥28. If BMI is in the 27.0–27.9 band, orlistat is eliminated.",
      trigger: {
        kind: "condition",
        evaluate: (ctx) => Number(ctx.context.bmi) < 28,
      },
    },
  ],
  eliminateOnNo: [],
  next: N_Q2A_COMORBIDITIES,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

// -----------------------------------------------------------------------------
// Q2 BMI <27 — established-patient continuation sub-flow
//
// Reached when BMI <27 (q2BmiBelow27Route branch "bmi_below_27").
//
// Clinical rule (source document, Q2 Outcome box and Note on BMI falling
// below threshold during treatment):
//   - BMI <27: all drugs excluded UNLESS already established on the drug.
//   - Tirzepatide: discontinue if BMI <22 during treatment.
//   - Semaglutide: discontinue if BMI <22.5 during treatment.
//   - Liraglutide, Mysimba, Orlistat: no explicit lower BMI threshold stated.
//
// Design notes:
//   1. The drug-pick node (N_Q2_ESTABLISHED_DRUG_PICK) is modelled as a
//      `single_select` so the engine stores the selected drug ID directly.
//   2. Continuation routes to N_DRUG_RESOLUTION (Stage 5), bypassing Stages
//      2–4 with only the established medicine remaining eligible.
//   3. Below-floor cases (tirz<22, sema<22.5) consolidate into the single
//      OUT_REFER_BMI_BELOW27 outcome. Audit distinguishes via the captured
//      drug pick and BMI value.
// -----------------------------------------------------------------------------

const q2ReinitFlag: FlagNode = {
  id: N_Q2_REINIT_FLAG,
  type: "flag",
  message:
    "Below the starting BMI: the restart or transfer criteria were confirmed in the shared assessment. Supply is only possible for the medicine already identified.",
  variant: "info",
  autoAdvance: true,
  auditFlags: ["bmi_below_27_returning_restart_criteria_met"],
  next: N_Q2_REINIT_LOCK_ROUTE,
};

// Floors repeat the shared assessment check so a missing earlier check cannot let a patient below the floor through.
// Mysimba and orlistat have no floor and only a transfer can reach them; a returning patient has no restart route for either.
const q2ReinitLockRoute: ConditionRouteNode = {
  id: N_Q2_REINIT_LOCK_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => lockedReturningOrTransferDrug(ctx.context) === "tirzepatide" && !(Number(ctx.context.bmi) >= 22),
      then: { outcomeId: O_REFER_BMI_BELOW27 },
    },
    {
      evaluate: (ctx) => {
        const drug = lockedReturningOrTransferDrug(ctx.context);
        return drug !== undefined && !["tirzepatide", "mysimba", "orlistat"].includes(drug) && !(Number(ctx.context.bmi) >= 22.5);
      },
      then: { outcomeId: O_REFER_BMI_BELOW27 },
    },
    { evaluate: (ctx) => lockedReturningOrTransferDrug(ctx.context) === "tirzepatide", then: N_Q2_CONT_TIRZ },
    { evaluate: (ctx) => lockedReturningOrTransferDrug(ctx.context) === "semaglutide", then: N_Q2_CONT_SEMA },
    { evaluate: (ctx) => lockedReturningOrTransferDrug(ctx.context) === "oral_semaglutide", then: N_Q2_CONT_OSEM },
    { evaluate: (ctx) => lockedReturningOrTransferDrug(ctx.context) === "foundayo", then: N_Q2_CONT_FDY },
    { evaluate: (ctx) => lockedReturningOrTransferDrug(ctx.context) === "liraglutide", then: N_Q2_CONT_LIRA },
    {
      evaluate: (ctx) => ctx.context["aht-sel-consultation-type"] === "transfer" && lockedReturningOrTransferDrug(ctx.context) === "mysimba",
      then: N_Q2_CONT_MYSI,
    },
    {
      evaluate: (ctx) => ctx.context["aht-sel-consultation-type"] === "transfer" && lockedReturningOrTransferDrug(ctx.context) === "orlistat",
      then: N_Q2_CONT_ORLI,
    },
  ],
  default: { outcomeId: O_REFER_BMI_BELOW27 },
};

const q2EstablishedCheck: YesNoNode = {
  id: N_Q2_ESTABLISHED_CHECK,
  type: "yes_no",
  text:
    "BMI is below 27 kg/m². Is the patient currently established on a weight management medicine under this PGD AND tolerating treatment well?",
  hint:
    "Answering 'No' will route to GP referral. Answering 'Yes' requires you to identify the medicine on the next screen. If you are uncertain whether the patient is tolerating treatment well (e.g. new side effects, missed doses, unexplained weight loss beyond expectation), answer 'No' and refer.",
  answerPolarity: "yes-positive",
  yes: N_Q2_ESTABLISHED_DRUG_PICK,
  no: { outcomeId: O_REFER_BMI_BELOW27 },
  auditFlags: {
    yes: "bmi_below_27_established_continuation_attested",
  },
};

const q2EstablishedDrugPick: SingleSelectNode = {
  id: N_Q2_ESTABLISHED_DRUG_PICK,
  type: "single_select",
  text:
    "Which medicine is the patient currently established on? Select one.",
  hint:
    "Identify the specific medicine. The selection drives the discontinuation-floor check (tirzepatide <22, semaglutide <22.5) and locks the eligible set for resolution.",
  options: [
    { id: "tirzepatide", label: "Tirzepatide (Mounjaro®)" },
    { id: "semaglutide", label: "Semaglutide (Wegovy®)" },
    { id: "foundayo", label: "Orforglipron (Foundayo®)" },
    { id: "liraglutide", label: "Liraglutide (Saxenda® / Biolide®)" },
    { id: "mysimba", label: "Mysimba® (naltrexone/bupropion)" },
    { id: "orlistat", label: "Orlistat (Xenical®)" },
  ],
  storesTo: "q2EstablishedDrugPick",
  required: true,
  routing: {
    tirzepatide: N_Q2_ESTABLISHED_FLOOR_ROUTE,
    semaglutide: N_Q2_ESTABLISHED_FLOOR_ROUTE,
    foundayo: N_Q2_ESTABLISHED_FLOOR_ROUTE,
    liraglutide: N_Q2_ESTABLISHED_FLOOR_ROUTE,
    mysimba: N_Q2_ESTABLISHED_FLOOR_ROUTE,
    orlistat: N_Q2_ESTABLISHED_FLOOR_ROUTE,
  },
};

const q2EstablishedFloorRoute: ConditionRouteNode = {
  id: N_Q2_ESTABLISHED_FLOOR_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) =>
        ctx.context.q2EstablishedDrugPick === "tirzepatide" &&
        Number(ctx.context.bmi) < 22,
      then: { outcomeId: O_REFER_BMI_BELOW27 },
    },
    {
      evaluate: (ctx) =>
        ctx.context.q2EstablishedDrugPick === "semaglutide" &&
        Number(ctx.context.bmi) < 22.5,
      then: { outcomeId: O_REFER_BMI_BELOW27 },
    },
    {
      evaluate: (ctx) =>
        ctx.context.q2EstablishedDrugPick === "foundayo" &&
        Number(ctx.context.bmi) < 22.5,
      then: { outcomeId: O_REFER_BMI_BELOW27 },
    },
    {
      evaluate: (ctx) => ctx.context.q2EstablishedDrugPick === "tirzepatide",
      then: N_Q2_CONT_TIRZ,
    },
    {
      evaluate: (ctx) => ctx.context.q2EstablishedDrugPick === "semaglutide",
      then: N_Q2_CONT_SEMA,
    },
    {
      evaluate: (ctx) => ctx.context.q2EstablishedDrugPick === "foundayo",
      then: N_Q2_CONT_FDY,
    },
    {
      evaluate: (ctx) => ctx.context.q2EstablishedDrugPick === "liraglutide",
      then: N_Q2_CONT_LIRA,
    },
    {
      evaluate: (ctx) => ctx.context.q2EstablishedDrugPick === "mysimba",
      then: N_Q2_CONT_MYSI,
    },
    {
      evaluate: (ctx) => ctx.context.q2EstablishedDrugPick === "orlistat",
      then: N_Q2_CONT_ORLI,
    },
  ],
  default: { outcomeId: O_REFER_BMI_BELOW27 }, // Defensive fallback; drug-pick branches above are exhaustive.
};

const q2ContOsem: DrugEliminationNode = {
  id: N_Q2_CONT_OSEM,
  type: "drug_elimination",
  text:
    "Returning-patient restart at BMI <27: the patient restarts their previous medicine. All other drugs are eliminated from the eligible set so the resolution node confirms the restart without ranking.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-returning-restart-keep-oral-semaglutide-eliminate-others",
      drugIds: ["tirzepatide", "semaglutide", "foundayo", "liraglutide", "mysimba", "orlistat"],
      reason: "Returning oral semaglutide restart at BMI <27: all other drugs eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["bmi_below_27_established_continuation_resolved"],
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: { outcomeId: O_REFER_BMI_BELOW27 },
};

const q2ContTirz: DrugEliminationNode = {
  id: N_Q2_CONT_TIRZ,
  type: "drug_elimination",
  text:
    "Established-patient continuation at BMI <27: the patient remains on their existing medicine. All other drugs are eliminated from the eligible set so the resolution node confirms continuation without ranking.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-established-continuation-keep-tirzepatide-eliminate-others",
      drugIds: ["semaglutide", "oral_semaglutide", "foundayo", "liraglutide", "mysimba", "orlistat"],
      reason: "Established tirzepatide continuation at BMI <27: all other drugs eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["bmi_below_27_established_continuation_resolved"],
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2ContSema: DrugEliminationNode = {
  id: N_Q2_CONT_SEMA,
  type: "drug_elimination",
  text:
    "Established-patient continuation at BMI <27: the patient remains on their existing medicine. All other drugs are eliminated from the eligible set so the resolution node confirms continuation without ranking.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-established-continuation-keep-semaglutide-eliminate-others",
      drugIds: ["tirzepatide", "oral_semaglutide", "foundayo", "liraglutide", "mysimba", "orlistat"],
      reason: "Established semaglutide continuation at BMI <27: all other drugs eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["bmi_below_27_established_continuation_resolved"],
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2ContLira: DrugEliminationNode = {
  id: N_Q2_CONT_LIRA,
  type: "drug_elimination",
  text:
    "Established-patient continuation at BMI <27: the patient remains on their existing medicine. All other drugs are eliminated from the eligible set so the resolution node confirms continuation without ranking.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-established-continuation-keep-liraglutide-eliminate-others",
      drugIds: ["tirzepatide", "semaglutide", "oral_semaglutide", "foundayo", "mysimba", "orlistat"],
      reason: "Established liraglutide continuation at BMI <27: all other drugs eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["bmi_below_27_established_continuation_resolved"],
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2ContMysi: DrugEliminationNode = {
  id: N_Q2_CONT_MYSI,
  type: "drug_elimination",
  text:
    "Established-patient continuation at BMI <27: the patient remains on their existing medicine. All other drugs are eliminated from the eligible set so the resolution node confirms continuation without ranking.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-established-continuation-keep-mysimba-eliminate-others",
      drugIds: ["tirzepatide", "semaglutide", "oral_semaglutide", "foundayo", "liraglutide", "orlistat"],
      reason: "Established mysimba continuation at BMI <27: all other drugs eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["bmi_below_27_established_continuation_resolved"],
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2ContOrli: DrugEliminationNode = {
  id: N_Q2_CONT_ORLI,
  type: "drug_elimination",
  text:
    "Established-patient continuation at BMI <27: the patient remains on their existing medicine. All other drugs are eliminated from the eligible set so the resolution node confirms continuation without ranking.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-established-continuation-keep-orlistat-eliminate-others",
      drugIds: ["tirzepatide", "semaglutide", "oral_semaglutide", "foundayo", "liraglutide", "mysimba"],
      reason: "Established orlistat continuation at BMI <27: all other drugs eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["bmi_below_27_established_continuation_resolved"],
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2ContFoundayo: DrugEliminationNode = {
  id: N_Q2_CONT_FDY,
  type: "drug_elimination",
  text:
    "Established-patient continuation at BMI <27: the patient remains on their existing medicine. All other drugs are eliminated from the eligible set so the resolution node confirms continuation without ranking.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2-established-continuation-keep-foundayo-eliminate-others",
      drugIds: ["tirzepatide", "semaglutide", "oral_semaglutide", "liraglutide", "mysimba", "orlistat"],
      reason: "Established Foundayo continuation at BMI <27: all other drugs eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["bmi_below_27_established_continuation_resolved"],
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

// -----------------------------------------------------------------------------
// Q2a — Comorbidity checklist
// -----------------------------------------------------------------------------

const q2aComorbidities: MultiSelectNode = {
  id: N_Q2A_COMORBIDITIES,
  type: "multi_select",
  text:
    "Q2a — Does the patient have any of the following weight-related comorbidities? Select all that apply.",
  hint:
    "If selecting Type 2 diabetes, an additional HbA1c question will follow. Uncontrolled hypertension will be re-checked at Q12 as a specific Mysimba exclusion.",
  items: [
    { id: "t2dm", text: "Type 2 diabetes mellitus (T2DM)" },
    {
      id: "prediabetes",
      text: "Prediabetes (HbA1c 42–47 mmol/mol or fasting glucose 5.6–6.9 mmol/L)",
    },
    {
      id: "hypertension",
      text:
        "Hypertension (controlled or uncontrolled — uncontrolled is a specific Mysimba exclusion at Q12)",
    },
    { id: "dyslipidaemia", text: "Dyslipidaemia" },
    { id: "obstructive_sleep_apnoea", text: "Obstructive sleep apnoea (OSA)" },
    { id: "cardiovascular_disease", text: "Cardiovascular disease (CVD)" },
    { id: "nafld", text: "Non-alcoholic fatty liver disease (NAFLD/NASH)" },
    { id: "osteoarthritis", text: "Osteoarthritis" },
    { id: "pcos", text: "Polycystic ovary syndrome (PCOS)" },
  ],
  noneOption: { label: "None of the above" },
  storesTo: "q2aComorbiditySelections",
  routing: (selections) =>
    selections.length > 0
      ? N_Q2A_LIRAGLUTIDE_QUALIFYING_ELIM
      : N_Q2A_NO_COMORBIDITY_ROUTE,
};

const q2aLiraglutideQualifyingElim: DrugEliminationNode = {
  id: N_Q2A_LIRAGLUTIDE_QUALIFYING_ELIM,
  type: "drug_elimination",
  text:
    "Liraglutide at BMI 27–29.9 (outside ethnic adjustment band) requires one of: dysglycaemia (T2DM or prediabetes), hypertension, dyslipidaemia, or OSA. Other comorbidities do not qualify liraglutide at this BMI band.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2a-liraglutide-no-qualifying-comorbidity-bmi-27-to-29",
      drugIds: ["liraglutide"],
      reason:
        "Liraglutide at BMI 27–29.9 (outside ethnic adjustment band) requires one of: dysglycaemia (T2DM or prediabetes), hypertension, dyslipidaemia, or OSA. Other comorbidities do not qualify liraglutide at this BMI band.",
      trigger: {
        kind: "condition",
        evaluate: (ctx) => {
          const bmi = Number(ctx.context.bmi ?? 0);
          const ethnicGroup = getPatientEthnicAdjustmentGroup(ctx.patient);
          if (bmi >= 30) return false;
          if (bmi < 27) return false;
          if (isEthnicAdjustedTo30(bmi, ethnicGroup)) return false;
          const selected = (ctx.context.q2aComorbiditySelections ?? []) as string[];
          return !selected.some((id) =>
            [
              "t2dm",
              "prediabetes",
              "hypertension",
              "dyslipidaemia",
              "obstructive_sleep_apnoea",
            ].includes(id)
          );
        },
      },
    },
  ],
  eliminateOnNo: [],
  next: N_Q2A_T2DM_ROUTE,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2aT2dmRoute: ConditionRouteNode = {
  id: N_Q2A_T2DM_ROUTE,
  type: "condition_route",
  // Seed "No" so the PGD pre-supply check for T2DM + HbA1c ≥86 can auto-answer
  // for patients who never reach Q2b. The T2DM branch overwrites it at
  // N_Q2B_HBA1C_ROUTE once the actual HbA1c is known.
  storesTo: "t2dmHba1cHigh",
  storedValue: () => false,
  conditions: [
    {
      evaluate: (ctx) =>
        ((ctx.context.q2aComorbiditySelections ?? []) as string[]).includes("t2dm"),
      then: N_Q2B_HBA1C_ENTRY,
    },
    {
      evaluate: () => true,
      then: N_Q3_THYROID,
    },
  ],
  default: { outcomeId: O_REFER_GP }, // Defensive fallback; T2DM selected/not-selected branches above are exhaustive.
};

const q2aNoComorbidityRoute: ConditionRouteNode = {
  id: N_Q2A_NO_COMORBIDITY_ROUTE,
  type: "condition_route",
  // Reached only when no comorbidity was selected, so T2DM is ruled out and
  // Q2b is never asked — seed "No" for the PGD pre-supply check.
  storesTo: "t2dmHba1cHigh",
  storedValue: () => false,
  conditions: [
    {
      evaluate: (ctx) => Number(ctx.context.bmi ?? 0) >= 30,
      then: N_Q3_THYROID,
    },
    {
      evaluate: (ctx) => {
        const bmi = Number(ctx.context.bmi ?? 0);
        const ethnicGroup = getPatientEthnicAdjustmentGroup(ctx.patient);
        return isEthnicAdjustedTo30(bmi, ethnicGroup);
      },
      then: N_Q2A_NO_COMORBIDITY_ETHNIC_ELIM,
    },
    {
      evaluate: () => true,
      then: { outcomeId: O_REFER_GP },
    },
  ],
  default: { outcomeId: O_REFER_GP }, // Defensive fallback; BMI/no-comorbidity branches above are exhaustive after BMI capture.
};

const q2aNoComorbidityEthnicElim: DrugEliminationNode = {
  id: N_Q2A_NO_COMORBIDITY_ETHNIC_ELIM,
  type: "drug_elimination",
  text:
    "BMI 27.5–29.9 in ethnic adjustment group, no comorbidity present: tirzepatide, semaglutide, and liraglutide remain eligible (treated as BMI ≥30). Mysimba requires comorbidity at standard threshold (excluded). Orlistat requires BMI ≥28 AND comorbidity at standard threshold (excluded).",
  autoAdvance: true,
  auditFlags: "q2a_foundayo_off_label_ethnic_band",
  eliminateOnYes: [
    {
      id: "Q2a-no-comorbidity-ethnic-adj-excludes-mysimba",
      drugIds: ["mysimba"],
      reason:
        "BMI 27.5–29.9 in ethnic adjustment group, no comorbidity present: tirzepatide, semaglutide, and liraglutide remain eligible (treated as BMI ≥30). Mysimba requires comorbidity at standard threshold (excluded). Orlistat requires BMI ≥28 AND comorbidity at standard threshold (excluded).",
      trigger: { kind: "always" },
    },
    {
      id: "Q2a-no-comorbidity-ethnic-adj-excludes-orlistat",
      drugIds: ["orlistat"],
      reason:
        "BMI 27.5–29.9 in ethnic adjustment group, no comorbidity present: tirzepatide, semaglutide, and liraglutide remain eligible (treated as BMI ≥30). Mysimba requires comorbidity at standard threshold (excluded). Orlistat requires BMI ≥28 AND comorbidity at standard threshold (excluded).",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q3_THYROID,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

// -----------------------------------------------------------------------------
// Q2b — HbA1c (only reached if T2DM selected at Q2a)
// -----------------------------------------------------------------------------

const q2bHba1cEntry: NumericInputNode = {
  id: N_Q2B_HBA1C_ENTRY,
  type: "numeric_input",
  text: "Q2b — Current HbA1c (mmol/mol)",
  hint:
    "Record most recent HbA1c result. If <58 mmol/mol — well controlled; 58–85 — flag for GP review; ≥86 — excludes tirzepatide, semaglutide, liraglutide, and mysimba.",
  unit: "mmol/mol",
  decimals: 0,
  hardMin: 20,
  hardMax: 200,
  plausibilityBands: [
    { tier: "silent_accept", min: 30, max: 130 },
    {
      tier: "confirmation_prompt",
      min: 20,
      max: 200,
      message:
        "HbA1c is outside the usual plausibility range. Confirm the entered HbA1c is correct.",
    },
  ],
  storesTo: "hba1cMmolMol",
  next: N_Q2B_HBA1C_RECENCY,
};

// Foundayo only: PGD v2.1 excludes type 2 diabetes with no HbA1c in the preceding 6 months.
// SIGN-OFF REQUIRED: OI-F6 — a yes/no recency answer; no result date is stored.
const q2bHba1cRecency: YesNoNode = {
  id: N_Q2B_HBA1C_RECENCY,
  type: "yes_no",
  text: "Q2b — Was this HbA1c result recorded within the last 6 months?",
  hint:
    "Orforglipron (Foundayo®) cannot be supplied to a patient with type 2 diabetes unless an HbA1c from the preceding 6 months is available. If you are not sure of the date, answer No. This does not affect the other medicines.",
  answerPolarity: "yes-positive",
  storesTo: "hba1cWithin6Months",
  yes: N_Q2B_HBA1C_ROUTE,
  no: N_Q2B_HBA1C_STALE_ELIM,
};

const q2bHba1cStaleElim: DrugEliminationNode = {
  id: N_Q2B_HBA1C_STALE_ELIM,
  type: "drug_elimination",
  text: "Type 2 diabetes with no HbA1c in the last 6 months: orforglipron (Foundayo®) excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2b-hba1c-not-recent-excludes-foundayo",
      drugIds: ["foundayo"],
      reason:
        "Type 2 diabetes with no HbA1c result from within the preceding 6 months: excluded under the Foundayo PGD. Arrange an HbA1c with the GP or diabetes team and re-screen.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q2B_HBA1C_ROUTE,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2bHba1cRoute: ConditionRouteNode = {
  id: N_Q2B_HBA1C_ROUTE,
  type: "condition_route",
  storesTo: "t2dmHba1cHigh",
  storedValue: (ctx) =>
    ((ctx.context.q2aComorbiditySelections ?? []) as string[]).includes("t2dm")
    && Number(ctx.context.hba1cMmolMol ?? 0) >= 86,
  conditions: [
    {
      evaluate: (ctx) => Number(ctx.context.hba1cMmolMol ?? 0) < 58,
      then: N_Q3_THYROID,
    },
    {
      evaluate: (ctx) => {
        const h = Number(ctx.context.hba1cMmolMol ?? 0);
        return h >= 58 && h <= 85;
      },
      then: N_Q2B_HBA1C_MID_FLAG,
    },
    {
      evaluate: (ctx) => Number(ctx.context.hba1cMmolMol ?? 0) >= 86,
      then: N_Q2B_HBA1C_HIGH_ELIM,
    },
  ],
  default: { outcomeId: O_REFER_GP }, // Defensive fallback; HbA1c bands above are exhaustive after HbA1c capture.
};

const q2bHba1cMidFlag: FlagNode = {
  id: N_Q2B_HBA1C_MID_FLAG,
  type: "flag",
  message:
    "HbA1c 58–85 mmol/mol: T2DM not optimally controlled. All medicines under this service remain potentially eligible. Flagged for GP review of antidiabetic regimen at initiation. Caution noted for Mysimba (hypoglycaemia risk on dose escalation).",
  variant: "warn",
  autoAdvance: true,
  auditFlags: ["q2b_hba1c_mid_band_gp_review_required"],
  next: N_Q3_THYROID,
};

const q2bHba1cHighElim: DrugEliminationNode = {
  id: N_Q2B_HBA1C_HIGH_ELIM,
  type: "drug_elimination",
  text:
    "HbA1c ≥86 mmol/mol: T2DM severely uncontrolled — refer to GP/diabetologist for specialist management. Tirzepatide, semaglutide, liraglutide, and mysimba are eliminated. Orlistat may remain eligible if BMI threshold met.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q2b-hba1c-high-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason:
        "HbA1c ≥86 mmol/mol: T2DM severely uncontrolled — refer to GP/diabetologist for specialist management. Tirzepatide, semaglutide, liraglutide, and mysimba are eliminated. Orlistat may remain eligible if BMI threshold met.",
      trigger: { kind: "always" },
    },
    {
      id: "Q2b-hba1c-high-excludes-semaglutide",
      drugIds: ["semaglutide", "oral_semaglutide"],
      reason:
        "HbA1c ≥86 mmol/mol: T2DM severely uncontrolled — refer to GP/diabetologist for specialist management. Tirzepatide, semaglutide, liraglutide, and mysimba are eliminated. Orlistat may remain eligible if BMI threshold met.",
      trigger: { kind: "always" },
    },
    {
      id: "Q2b-hba1c-high-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason:
        "HbA1c ≥86 mmol/mol: T2DM severely uncontrolled — refer to GP/diabetologist for specialist management. Tirzepatide, semaglutide, liraglutide, and mysimba are eliminated. Orlistat may remain eligible if BMI threshold met.",
      trigger: { kind: "always" },
    },
    {
      id: "Q2b-hba1c-high-excludes-mysimba",
      drugIds: ["mysimba"],
      reason:
        "HbA1c ≥86 mmol/mol: T2DM severely uncontrolled — refer to GP/diabetologist for specialist management. Tirzepatide, semaglutide, liraglutide, and mysimba are eliminated. Orlistat may remain eligible if BMI threshold met.",
      trigger: { kind: "always" },
    },
    {
      // The Foundayo PGD threshold is ABOVE 86 mmol/mol (exactly 86 is not excluded), unlike the ≥86 used above.
      // Safe to read hba1cMmolMol here: it is stored by the entry node, a user-answered node before this hop chain.
      id: "Q2b-hba1c-above-86-excludes-foundayo",
      drugIds: ["foundayo"],
      reason:
        "HbA1c above 86 mmol/mol (10%): T2DM severely uncontrolled — excluded under the Foundayo PGD. Refer to GP/diabetologist for specialist management.",
      trigger: {
        kind: "condition",
        evaluate: (ctx) => Number(ctx.context.hba1cMmolMol ?? 0) > 86,
      },
    },
  ],
  eliminateOnNo: [],
  auditFlags: ["q2b_hba1c_high_specialist_referral_required"],
  next: N_Q2B_HBA1C_HIGH_ORLISTAT_CHECK,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
};

const q2bHba1cHighOrlistatCheck: ConditionRouteNode = {
  id: N_Q2B_HBA1C_HIGH_ORLISTAT_CHECK,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.includes("orlistat") || ctx.eligibleDrugIds.includes("foundayo"),
      then: N_Q3_THYROID,
    },
    {
      evaluate: () => true,
      then: { outcomeId: O_REFER_GP },
    },
  ],
  default: { outcomeId: O_REFER_GP }, // Defensive fallback; orlistat/foundayo present/absent branches above are exhaustive.
};

// =============================================================================
// ARCHITECTURE RULE — autoAdvance drug_elimination trigger kinds
// =============================================================================
//
// autoAdvance drug_elimination nodes evaluate triggers BEFORE the React state
// update from the preceding node has committed. This means:
//
//   trigger: { kind: "condition", evaluate: (ctx) => ctx.context.someKey }
//
// will see a STALE value for someKey if that key was set by a storesTo or
// storesMany on the immediately-preceding node in the same hop chain —
// even though accumulatedContext carries the value correctly through
// condition_route hops, the computeEliminationAnswer call inside the
// advanceTo loop may not see it reliably in all cases.
//
// SAFE patterns:
//   - trigger: { kind: "always" } — unconditional, always correct
//   - trigger: { kind: "condition" } reading ctx.context.bmi or other values
//     that are in contextValues BEFORE the hop chain starts (e.g. vitals)
//   - No trigger at all (only valid for non-autoAdvance nodes with explicit
//     yes/no answers)
//
// UNSAFE pattern (do not use):
//   - trigger: { kind: "condition" } reading a value set by storesTo or
//     storesMany on a node earlier in the SAME hop chain
//
// CORRECT approach when conditional elimination is needed:
//   Use an upstream condition_route node to gate which elimination node is
//   reached, then use trigger: { kind: "always" } on the elimination node.
//   See q14GiToQ16Route, q17ToQ18Route, q17ToQ19Route, q18ToQ19Route,
//   q2EstablishedFloorRoute, and qp2WeeklyVsDaily/qp3OralPreference routing
//   for examples of this pattern.
//
// =============================================================================

// -----------------------------------------------------------------------------
// Stage 2 — Contraindication screening (Q3–Q19)
// -----------------------------------------------------------------------------

// ─────────────────────────────────────────────────────────────────────────────
// Group A — GLP-1 contraindications (Q3, Q4, Q5)
// All three eliminate tirzepatide, semaglutide, liraglutide.
// Entry point: N_Q3_THYROID (preserved — upstream routing unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const q3GroupAChecklist: MultiSelectNode = {
  id: N_Q3_THYROID,
  type: "multi_select",
  text: "Q3–5 — Does the patient have any of the following? Select all that apply.",
  hint: "These conditions contraindicate GLP-1 receptor agonists (tirzepatide, semaglutide, liraglutide). Mysimba and orlistat are not affected by these contraindications. Foundayo is not removed by MTC/MEN2 (its PGD has no such exclusion) but is removed by pancreatitis and by type 1 or type 3c diabetes.",
  items: [
    {
      id: "q3_thyroid",
      text: "Personal or family history of medullary thyroid carcinoma (MTC) or Multiple Endocrine Neoplasia syndrome type 2 (MEN2)",
    },
    {
      id: "q4_pancreatitis",
      text: "History of pancreatitis (acute or chronic)",
    },
    {
      id: "q5_t1dm_t3c",
      text: "Type 1 diabetes (T1DM) or Type 3c (pancreatogenic) diabetes",
    },
  ],
  noneOption: { label: "None of the above" },
  storesTo: "q3to5GroupASelections",
  // Individual booleans so each downstream PGD pre-supply check can
  // autoAnswerFrom its own criterion rather than re-asking the pharmacist.
  storesMany: {
    q3Thyroid: (ids: string[]) => ids.includes("q3_thyroid"),
    q4Pancreatitis: (ids: string[]) => ids.includes("q4_pancreatitis"),
    q5T1dmT3c: (ids: string[]) => ids.includes("q5_t1dm_t3c"),
  },
  routing: (selections) => {
    if (selections.includes("q3_thyroid")) return N_Q3_GROUP_A_ROUTE;
    if (selections.includes("q4_pancreatitis")) return N_Q3_GROUP_A_ROUTE;
    if (selections.includes("q5_t1dm_t3c")) return N_Q3_GROUP_A_ROUTE;
    return N_Q6_SULPHONYLUREAS_INSULIN;
  },
};

const q3GroupARoute: ConditionRouteNode = {
  id: N_Q3_GROUP_A_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ((ctx.context["q3to5GroupASelections"] as string[]) ?? []).includes("q3_thyroid"),
      then: N_Q3_THYROID_ELIM,
    },
    {
      evaluate: (ctx) => ((ctx.context["q3to5GroupASelections"] as string[]) ?? []).includes("q4_pancreatitis"),
      then: N_Q4_PANCREATITIS_ELIM,
    },
    {
      evaluate: (ctx) => ((ctx.context["q3to5GroupASelections"] as string[]) ?? []).includes("q5_t1dm_t3c"),
      then: N_Q5_T1DM_ELIM,
    },
  ],
  default: N_Q6_SULPHONYLUREAS_INSULIN,
};

const q3ThyroidElim: DrugEliminationNode = {
  id: N_Q3_THYROID_ELIM,
  type: "drug_elimination",
  text: "Q3 — MTC/MEN2 history: GLP-1 agents excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q3-MTC-MEN2-excludes-tirzepatide", drugIds: ["tirzepatide"], reason: "YES at Q3: GLP-1 receptor agonists contraindicated — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q3-MTC-MEN2-excludes-semaglutide", drugIds: ["semaglutide", "oral_semaglutide"], reason: "YES at Q3: GLP-1 receptor agonists contraindicated — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q3-MTC-MEN2-excludes-liraglutide", drugIds: ["liraglutide"], reason: "YES at Q3: GLP-1 receptor agonists contraindicated — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  next: N_Q4_PANCREATITIS_ELIM,
  noDrugsRemaining: N_Q4_PANCREATITIS_ELIM,
};

const q4PancreatitisElim: DrugEliminationNode = {
  id: N_Q4_PANCREATITIS_ELIM,
  type: "drug_elimination",
  text: "Q4 — Pancreatitis history: GLP-1 agents excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q4-pancreatitis-excludes-tirzepatide", drugIds: ["tirzepatide"], reason: "YES at Q4: history of pancreatitis — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q4-pancreatitis-excludes-semaglutide", drugIds: ["semaglutide", "oral_semaglutide"], reason: "YES at Q4: history of pancreatitis — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q4-pancreatitis-excludes-liraglutide", drugIds: ["liraglutide"], reason: "YES at Q4: history of pancreatitis — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q4-pancreatitis-excludes-foundayo", drugIds: ["foundayo"], reason: "YES at Q4: history of pancreatitis — excluded under the Foundayo PGD (not studied in patients with a history of pancreatitis).", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  next: N_Q5_T1DM_ELIM,
  noDrugsRemaining: N_Q5_T1DM_ELIM,
};

const q5T1dmElim: DrugEliminationNode = {
  id: N_Q5_T1DM_ELIM,
  type: "drug_elimination",
  text: "Q5 — T1DM/T3cDM: GLP-1 agents excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q5-T1DM-T3c-excludes-tirzepatide", drugIds: ["tirzepatide"], reason: "YES at Q5: T1DM or T3cDM present — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q5-T1DM-T3c-excludes-semaglutide", drugIds: ["semaglutide", "oral_semaglutide"], reason: "YES at Q5: T1DM or T3cDM present — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q5-T1DM-T3c-excludes-liraglutide", drugIds: ["liraglutide"], reason: "YES at Q5: T1DM or T3cDM present — eliminate tirzepatide, semaglutide, and liraglutide.", trigger: { kind: "always" } },
    { id: "Q5-T1DM-T3c-excludes-foundayo", drugIds: ["foundayo"], reason: "YES at Q5: type 1 or type 3c diabetes — excluded under the Foundayo PGD.", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  next: N_Q6_SULPHONYLUREAS_INSULIN,
  noDrugsRemaining: N_Q6_SULPHONYLUREAS_INSULIN,
};

const q6SulphonylureasInsulin: DrugEliminationNode = {
  id: N_Q6_SULPHONYLUREAS_INSULIN,
  type: "drug_elimination",
  storesTo: "q6SulphonylureaInsulin",
  text:
    "Q6 — Is the patient currently using sulphonylureas (e.g. gliclazide, glibenclamide) or insulin?",
  hint:
    "Combining GLP-1 receptor agonists or tirzepatide with sulphonylureas or insulin substantially increases hypoglycaemia risk — requires specialist oversight; refer to GP. Mysimba may also affect glycaemic control — flag for GP review.",
  eliminateOnYes: [
    {
      id: "Q6-sulphonylurea-insulin-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason:
        "YES at Q6: concurrent sulphonylurea or insulin — eliminate tirzepatide, semaglutide, and liraglutide. Mysimba flagged for caution (monitor glycaemic control on dose escalation).",
    },
    {
      id: "Q6-sulphonylurea-insulin-excludes-semaglutide",
      drugIds: ["semaglutide"],
      reason:
        "YES at Q6: concurrent sulphonylurea or insulin — eliminate tirzepatide, semaglutide, and liraglutide. Mysimba flagged for caution (monitor glycaemic control on dose escalation).",
    },
    {
      id: "Q6-sulphonylurea-insulin-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason:
        "YES at Q6: concurrent sulphonylurea or insulin — liraglutide excluded (increased hypoglycaemia risk — requires specialist oversight; refer to GP). Tirzepatide and semaglutide also excluded. Mysimba flagged for caution (hypoglycaemia risk — flag for GP review).",
    },
    {
      id: "Q6-sulphonylureas-insulin-excludes-oral-semaglutide",
      drugIds: ["oral_semaglutide"],
      reason:
        "Sulphonylurea or insulin use is an exclusion for oral semaglutide (Wegovy® tablets) under the oral PGD.",
    },
    {
      id: "Q6-sulphonylureas-insulin-excludes-foundayo",
      drugIds: ["foundayo"],
      reason:
        "Insulin or sulfonylurea use is an exclusion under the Foundayo PGD. Reducing these doses is a prescribing decision outside the PGD — refer to GP.",
    },
  ],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q6_mysimba_hypoglycaemia_caution",
  },
  next: N_Q7_RENAL_HEPATIC,
  noDrugsRemaining: N_Q7_RENAL_HEPATIC,
};

// Q7 applies EVERY ticked option, in this fixed order. Routing enters at the first ticked option's node;
// each node then continues to the next ticked option via a router, ending at Q8. The result is the union of
// all ticked options' exclusions and flags, whatever the combination. The live preview reads these same nodes.
const Q7_OPTION_ORDER: { optionId: string; nodeId: string }[] = [
  { optionId: "severe_renal", nodeId: N_Q7_SEVERE_RENAL_ELIM },
  { optionId: "liraglutide_renal_threshold", nodeId: N_Q7_LIRAGLUTIDE_RENAL_ELIM },
  { optionId: "oral_sema_egfr_below_30", nodeId: N_Q7_ORAL_SEMA_EGFR_ELIM },
  { optionId: "moderate_renal", nodeId: N_Q7_MODERATE_RENAL_FLAG },
  { optionId: "moderate_hepatic", nodeId: N_Q7_MOD_HEPATIC_ELIM },
  { optionId: "severe_hepatic", nodeId: N_Q7_HEPATIC_ELIM },
];
const q7RouterId = (i: number): string => `Q7_route_after_${Q7_OPTION_ORDER[i]!.optionId}`;
const q7NextAfter = (i: number): string =>
  i === Q7_OPTION_ORDER.length - 1 ? N_Q8_SEIZURES_CNS : q7RouterId(i);
const q7FirstTickedNode = (selections: string[]): string =>
  Q7_OPTION_ORDER.find((o) => selections.includes(o.optionId))?.nodeId ?? N_Q8_SEIZURES_CNS;

// Q7 — Renal and hepatic assessment — multi-select
// Entry point ID unchanged so Q6 routing is preserved.
const q7RenalHepatic: MultiSelectNode = {
  id: N_Q7_RENAL_HEPATIC,
  type: "multi_select",
  text: "Q7 — Does the patient have any of the following renal or hepatic conditions? Select all that apply.",
  hint: "Severe renal or hepatic impairment contraindicates most weight management medicines. Foundayo has no renal exclusion (no dose adjustment, including end-stage renal disease); only severe hepatic impairment (Child-Pugh C) excludes it. Liraglutide has a lower renal threshold than other GLP-1 agents. Moderate renal impairment requires Mysimba dose reduction. Moderate hepatic impairment (Child-Pugh B) excludes Mysimba only. Orlistat is excluded in severe hepatic impairment and in severe or end-stage renal disease (Orlistat PGD).",
  items: [
    {
      id: "severe_renal",
      text: "Severe renal impairment — CrCl <15 mL/min (excludes tirzepatide, both semaglutide products, liraglutide, Mysimba and orlistat; Foundayo is not excluded)",
    },
    {
      id: "liraglutide_renal_threshold",
      text: "Renal impairment CrCl 15–29 mL/min — below liraglutide's lower renal threshold (excludes liraglutide and oral semaglutide)",
    },
    {
      id: "oral_sema_egfr_below_30",
      text: "Renal impairment — eGFR below 30 mL/min/1.73m² (applicable to oral semaglutide Wegovy® tablets only — check eGFR where indicated; this threshold differs from the CrCl thresholds above)",
    },
    {
      id: "moderate_renal",
      text: "Moderate renal impairment — CrCl 30–59 mL/min (Mysimba dose reduction required: maximum 2 tablets/day)",
    },
    {
      id: "moderate_hepatic",
      text: "Moderate hepatic impairment — Child-Pugh B (excludes Mysimba)",
    },
    {
      id: "severe_hepatic",
      text: "Severe hepatic impairment — Child-Pugh C (excludes all seven medicines)",
    },
  ],
  noneOption: { label: "None of the above — no renal or hepatic impairment" },
  storesTo: "q7RenalHepatic",
  storesMany: {
    q7SevereRenal: (ids: string[]) => ids.includes("severe_renal"),
    oralSemaEgfrBelow30: (ids: string[]) => ids.includes("oral_sema_egfr_below_30"),
    q7SevereHepatic: (ids: string[]) => ids.includes("severe_hepatic"),
  },
  routing: (selections) => q7FirstTickedNode(selections),
};

const q7Routers: ConditionRouteNode[] = Q7_OPTION_ORDER.slice(0, -1).map((_, i): ConditionRouteNode => ({
  id: q7RouterId(i),
  type: "condition_route",
  conditions: Q7_OPTION_ORDER.slice(i + 1).map((o) => ({
    evaluate: (ctx) =>
      ((ctx.context["q7RenalHepatic"] as string[] | undefined) ?? []).includes(o.optionId),
    then: o.nodeId,
  })),
  default: N_Q8_SEIZURES_CNS,
}));

// Q7 — Severe renal elimination (CrCl <15)
// Liraglutide also eliminated here — CrCl <15 is below its CrCl <30 threshold.
const q7SevereRenalElim: DrugEliminationNode = {
  id: N_Q7_SEVERE_RENAL_ELIM,
  type: "drug_elimination",
  text: "Q7 — Severe renal impairment (CrCl <15 mL/min): all medicines except Foundayo excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q7-severe-renal-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason: "Q7: severe renal impairment (CrCl <15 mL/min) — tirzepatide excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-renal-excludes-semaglutide",
      drugIds: ["semaglutide"],
      reason: "Q7: severe renal impairment (CrCl <15 mL/min) — semaglutide excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-renal-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason: "Q7: severe renal impairment (CrCl <15 mL/min) — liraglutide excluded (threshold CrCl <30 mL/min).",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-renal-excludes-mysimba",
      drugIds: ["mysimba"],
      reason: "Q7: severe renal impairment (CrCl <15 mL/min) — mysimba excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-renal-excludes-orlistat",
      drugIds: ["orlistat"],
      reason: "Severe or end-stage renal disease — excluded per Orlistat PGD.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-renal-excludes-oral-semaglutide",
      drugIds: ["oral_semaglutide"],
      reason: "Severe renal impairment (CrCl <15, eGFR <15): oral semaglutide excluded — eGFR below 30 threshold met.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: q7NextAfter(0),
  noDrugsRemaining: q7NextAfter(0),
};

// Q7 — Liraglutide renal threshold elimination (CrCl 15–29)
const q7LiraglutideRenalElim: DrugEliminationNode = {
  id: N_Q7_LIRAGLUTIDE_RENAL_ELIM,
  type: "drug_elimination",
  text: "Q7 — Renal impairment CrCl 15–29 mL/min: liraglutide excluded (lower renal threshold than other GLP-1 agents).",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q7-liraglutide-renal-threshold-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason: "Q7: renal impairment CrCl 15–29 mL/min — liraglutide excluded at CrCl <30 mL/min threshold.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-liraglutide-renal-excludes-oral-semaglutide",
      drugIds: ["oral_semaglutide"],
      reason: "Renal impairment CrCl 15–29 mL/min (eGFR approximately 15–29): oral semaglutide excluded — eGFR below 30 threshold met.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: q7NextAfter(1),
  noDrugsRemaining: q7NextAfter(1),
};

const q7OralSemaEgfrElim: DrugEliminationNode = {
  id: N_Q7_ORAL_SEMA_EGFR_ELIM,
  type: "drug_elimination",
  text: "Q7 — eGFR below 30: oral semaglutide (Wegovy® tablets) excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q7-egfr-below-30-excludes-oral-semaglutide",
      drugIds: ["oral_semaglutide"],
      reason: "eGFR below 30 mL/min/1.73m²: oral semaglutide (Wegovy® tablets) excluded under the oral PGD.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: q7NextAfter(2),
  noDrugsRemaining: q7NextAfter(2),
};

// Q7 — Moderate renal impairment flag (CrCl 30–59) — no elimination
const q7ModerateRenalFlag: DrugEliminationNode = {
  id: N_Q7_MODERATE_RENAL_FLAG,
  type: "drug_elimination",
  text: "Q7 — Moderate renal impairment (CrCl 30–59 mL/min): Mysimba dose reduction required.",
  autoAdvance: true,
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: "q7_mysimba_dose_reduction_flag",
  next: q7NextAfter(3),
  noDrugsRemaining: q7NextAfter(3),
};

// Q7 — Severe hepatic elimination (Child-Pugh C)
const q7SevereHepaticElim: DrugEliminationNode = {
  id: N_Q7_HEPATIC_ELIM,
  type: "drug_elimination",
  text: "Q7 — Severe hepatic impairment (Child-Pugh C): all seven medicines excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q7-severe-hepatic-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason: "Q7: severe hepatic impairment (Child-Pugh C) — tirzepatide excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-hepatic-excludes-semaglutide",
      drugIds: ["semaglutide", "oral_semaglutide"],
      reason: "Q7: severe hepatic impairment (Child-Pugh C) — semaglutide excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-hepatic-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason: "Q7: severe hepatic impairment (Child-Pugh C) — liraglutide excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-hepatic-excludes-mysimba",
      drugIds: ["mysimba"],
      reason: "Q7: severe hepatic impairment (Child-Pugh C) — mysimba excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-hepatic-excludes-orlistat",
      drugIds: ["orlistat"],
      reason: "Severe hepatic impairment — excluded per Orlistat PGD.",
      trigger: { kind: "always" },
    },
    {
      id: "Q7-severe-hepatic-excludes-foundayo",
      drugIds: ["foundayo"],
      reason: "Q7: severe hepatic impairment (Child-Pugh C) — Foundayo excluded (not recommended in severe hepatic impairment).",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: q7NextAfter(5),
  noDrugsRemaining: q7NextAfter(5),
};

// Q7 — Moderate hepatic elimination (Child-Pugh B) — Mysimba only.
const q7ModerateHepaticElim: DrugEliminationNode = {
  id: N_Q7_MOD_HEPATIC_ELIM,
  type: "drug_elimination",
  text: "Q7 — Moderate hepatic impairment (Child-Pugh B): Mysimba excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q7-moderate-hepatic-excludes-mysimba",
      drugIds: ["mysimba"],
      reason: "Moderate hepatic impairment (Child-Pugh B) — Mysimba excluded per Mysimba PGD.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: q7NextAfter(4),
  noDrugsRemaining: q7NextAfter(4),
};

// ─────────────────────────────────────────────────────────────────────────────
// Group B — Mysimba CNS contraindications (Q8, Q10, Q11)
// All three eliminate Mysimba only.
// Entry point: N_Q8_SEIZURES_CNS (preserved — upstream routing unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const q8GroupBChecklist: MultiSelectNode = {
  id: N_Q8_SEIZURES_CNS,
  type: "multi_select",
  text: "Q8, 10, 11 — Does the patient have any of the following? Select all that apply.",
  hint: "These conditions contraindicate Mysimba (naltrexone/bupropion). Other weight management medicines are not affected by these contraindications.",
  items: [
    {
      id: "q8_seizures_cns",
      text: "Seizure disorder, history of seizures, or known CNS tumour",
    },
    {
      id: "q10_opioids",
      text: "Currently taking opioids or opiate agonists (prescription or otherwise), OR used any opioid in the last 7–10 days — e.g. codeine, tramadol, morphine, methadone, buprenorphine",
    },
    {
      id: "q11_maois",
      text: "Taken a monoamine oxidase inhibitor (MAOI) within the last 14 days — e.g. phenelzine, tranylcypromine, isocarboxazid, moclobemide, selegiline",
    },
  ],
  noneOption: { label: "None of the above" },
  storesTo: "q8to11GroupBSelections",
  storesMany: {
    q8SeizuresCns: (ids: string[]) => ids.includes("q8_seizures_cns"),
    q10Opioids:    (ids: string[]) => ids.includes("q10_opioids"),
    q11Maois:      (ids: string[]) => ids.includes("q11_maois"),
  },
  routing: (selections) =>
    selections.length > 0 ? N_Q8_GROUP_B_ROUTE : N_Q9A_EATING_DISORDER,
};

const q8GroupBRoute: ConditionRouteNode = {
  id: N_Q8_GROUP_B_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ((ctx.context["q8to11GroupBSelections"] as string[]) ?? []).includes("q8_seizures_cns"),
      then: N_Q8_SEIZURES_ELIM,
    },
    {
      evaluate: (ctx) => ((ctx.context["q8to11GroupBSelections"] as string[]) ?? []).includes("q10_opioids"),
      then: N_Q10_OPIOID_ELIM,
    },
    {
      evaluate: (ctx) => ((ctx.context["q8to11GroupBSelections"] as string[]) ?? []).includes("q11_maois"),
      then: N_Q11_MAOIS_ELIM,
    },
  ],
  default: N_Q9A_EATING_DISORDER,
};

const q8SeizuresElim: DrugEliminationNode = {
  id: N_Q8_SEIZURES_ELIM,
  type: "drug_elimination",
  text: "Q8 — Seizure disorder/CNS tumour: Mysimba excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q8-seizures-cns-tumour-excludes-mysimba", drugIds: ["mysimba"], reason: "YES at Q8: seizure risk — eliminate mysimba.", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  next: N_Q10_OPIOID_ELIM,
  noDrugsRemaining: N_Q10_OPIOID_ELIM,
};

const q10OpioidElim: DrugEliminationNode = {
  id: N_Q10_OPIOID_ELIM,
  type: "drug_elimination",
  text: "Q10 — Opioid use/withdrawal: Mysimba excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q10-opioid-use-excludes-mysimba", drugIds: ["mysimba"], reason: "Opioid use current or within the last 7–10 days — excluded per Mysimba PGD.", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  next: N_Q11_MAOIS_ELIM,
  noDrugsRemaining: N_Q11_MAOIS_ELIM,
};

const q11MaoisElim: DrugEliminationNode = {
  id: N_Q11_MAOIS_ELIM,
  type: "drug_elimination",
  text: "Q11 — Recent MAOI: Mysimba excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q11-MAOI-within-14-days-excludes-mysimba", drugIds: ["mysimba"], reason: "YES at Q11: recent MAOI exposure — eliminate mysimba.", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  next: N_Q9A_EATING_DISORDER,
  noDrugsRemaining: N_Q9A_EATING_DISORDER,
};

// Q9a — eating disorder. Yes → sign-off path, then Q9b. No → Q9b.
const q9aEatingDisorder: DrugEliminationNode = {
  id: N_Q9A_EATING_DISORDER,
  type: "drug_elimination",
  text:
    "Q9a — Does the patient have a current or previous diagnosis of bulimia nervosa, anorexia nervosa, or binge eating disorder?",
  hint:
    "Mysimba is absolutely contraindicated. For any remaining eligible drugs — do NOT proceed to supply without prior written confirmation from the eating disorder service or responsible clinician that pharmacological weight management is appropriate. Document upload is mandatory before proceeding.",
  eliminateOnYes: [
    {
      id: "Q9a-eating-disorder-excludes-mysimba",
      drugIds: ["mysimba"],
      reason:
        "YES at Q9a: eating disorder — eliminate mysimba. Liraglutide flagged for adult clinical-judgement caution.",
    },
  ],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q9_liraglutide_adult_eating_disorder_bipolar_caution",
  },
  answerRouting: {
    yes: N_Q9_EATING_DISORDER_UPLOAD,
    no: N_Q9B_BIPOLAR,
  },
  next: N_Q9B_BIPOLAR,
  noDrugsRemaining: N_Q9B_BIPOLAR,
};

// Q9b — bipolar disorder. Yes → sign-off path, then Q12. No → Q12.
const q9bBipolar: DrugEliminationNode = {
  id: N_Q9B_BIPOLAR,
  type: "drug_elimination",
  text: "Q9b — Does the patient have a history of bipolar disorder?",
  hint:
    "Mysimba is absolutely contraindicated. For any remaining eligible drugs — do NOT proceed to supply without prior written confirmation from the GP or psychiatrist that pharmacological weight management is appropriate. Document upload is mandatory before proceeding.",
  eliminateOnYes: [
    {
      id: "Q9b-bipolar-excludes-mysimba",
      drugIds: ["mysimba"],
      reason:
        "YES at Q9b: bipolar disorder — eliminate mysimba. Liraglutide flagged for adult clinical-judgement caution.",
    },
  ],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q9_liraglutide_adult_eating_disorder_bipolar_caution",
  },
  answerRouting: {
    yes: N_Q9_BIPOLAR_UPLOAD,
    no: N_Q9C_FDY_GUARD,
  },
  next: N_Q9C_FDY_GUARD,
  noDrugsRemaining: N_Q12_UNCONTROLLED_BP,
};

// Q9 eating disorder — document upload instruction
const q9EatingDisorderUpload: GatewayProgressionNode = {
  id: N_Q9_EATING_DISORDER_UPLOAD,
  type: "gateway_progression",
  heading: "Mandatory: upload eating disorder service sign-off",
  body: "Before any weight management medicine can be supplied, you must upload written confirmation from the patient's eating disorder service or responsible clinician that pharmacological weight management is appropriate for this patient.\n\nTo upload: use the Add Action button in the Actions panel \u2192 select Document Upload \u2192 upload the confirmation letter or email.\n\nDo not proceed until this document is uploaded.",
  triggersPayment: false,
  next: N_Q9_EATING_DISORDER_ATTEST,
};

// Q9 eating disorder — attestation
const q9EatingDisorderAttestation: AttestationNode = {
  id: N_Q9_EATING_DISORDER_ATTEST,
  type: "attestation",
  text: "Q9a \u2014 Eating disorder sign-off attestation",
  attestationStatement:
    "I confirm that I have received and uploaded written confirmation from the patient's eating disorder service or responsible clinician that pharmacological weight management is appropriate for this patient. This document is recorded in the patient record in the Actions panel. I have reviewed the document and am satisfied it authorises proceeding with weight management treatment.",
  conclusions: {
    optionA: {
      label: "Confirmed \u2014 document uploaded and reviewed",
      next: N_Q9B_BIPOLAR,
    },
    optionB: {
      label: "Cannot confirm \u2014 do not proceed",
      next: { outcomeId: O_REFER_GP },
    },
  },
};

// Q9 bipolar — document upload instruction
const q9BipolarUpload: GatewayProgressionNode = {
  id: N_Q9_BIPOLAR_UPLOAD,
  type: "gateway_progression",
  heading: "Mandatory: upload GP or psychiatrist sign-off",
  body: "Before any weight management medicine can be supplied, you must upload documented sign-off from the patient's GP or psychiatrist confirming that pharmacological weight management is appropriate for this patient.\n\nTo upload: use the Add Action button in the Actions panel \u2192 select Document Upload \u2192 upload the sign-off letter or clinical note.\n\nDo not proceed until this document is uploaded.",
  triggersPayment: false,
  next: N_Q9_BIPOLAR_ATTEST,
};

// Q9 bipolar — attestation
const q9BipolarAttestation: AttestationNode = {
  id: N_Q9_BIPOLAR_ATTEST,
  type: "attestation",
  text: "Q9b \u2014 Bipolar disorder sign-off attestation",
  attestationStatement:
    "I confirm that I have received and uploaded documented sign-off from the patient's GP or psychiatrist that pharmacological weight management is appropriate for this patient. This document is recorded in the patient record in the Actions panel. I have reviewed the document and am satisfied it authorises proceeding with weight management treatment.",
  conclusions: {
    optionA: {
      label: "Confirmed \u2014 document uploaded and reviewed",
      next: N_Q9C_FDY_GUARD,
    },
    optionB: {
      label: "Cannot confirm \u2014 do not proceed",
      next: { outcomeId: O_REFER_GP },
    },
  },
};

// Q10 and Q11 interactive nodes removed — replaced by Group B autoAdvance elim nodes above.

// Q12 — BP measurement instruction
// Entry point ID unchanged so Q11 routing is preserved.
const q12BpInstruction: GatewayProgressionNode = {
  id: N_Q12_UNCONTROLLED_BP,
  type: "gateway_progression",
  heading: "Q12 — Measure blood pressure now",
  body: "Blood pressure must be measured and recorded before Mysimba can be considered.\n\nRecord the BP reading using the vitals panel:\nActions panel \u2192 Add Vital \u2192 Blood Pressure\n\nThe reading will be stored in the patient record. Once recorded, tap Continue to confirm the result.",
  triggersPayment: false,
  plausibilityChecks: true,
  next: N_Q12_BP_DECISION,
};

// Q12 — BP controlled decision and attestation
const q12BpDecision: AttestationNode = {
  id: N_Q12_BP_DECISION,
  type: "attestation",
  text: "Q12 — Blood pressure result",
  attestationStatement:
    "I have measured and recorded the patient's blood pressure in the vitals panel. Based on the recorded reading, I confirm the blood pressure result below. Controlled = below 140/90 mmHg (or below 150/90 mmHg if aged 75 or over).",
  conclusions: {
    optionA: {
      label: "BP is controlled — below threshold for this patient's age",
      next: N_Q13_SUICIDAL_IDEATION,
    },
    optionB: {
      label: "BP is uncontrolled or above threshold — Mysimba excluded",
      next: N_Q12_BP_ELIM,
    },
  },
};

// Q12 — Mysimba elimination for uncontrolled BP
const q12BpMysimbaElim: DrugEliminationNode = {
  id: N_Q12_BP_ELIM,
  type: "drug_elimination",
  text: "Q12 — Uncontrolled blood pressure: Mysimba excluded.",
  hint: "Bupropion (Mysimba component) can raise BP and is contraindicated in uncontrolled hypertension. Post-marketing reports of hypertensive crisis during titration. Mysimba eliminated — other eligible drugs may still proceed. Refer to GP for BP management if appropriate.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q12-uncontrolled-BP-excludes-mysimba",
      drugIds: ["mysimba"],
      reason:
        "Q12: pharmacist confirmed BP above controlled threshold — eliminate mysimba. Post-marketing hypertensive crisis reported during titration. BP must be controlled before Mysimba can be considered.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q13_SUICIDAL_IDEATION,
  noDrugsRemaining: N_Q13_SUICIDAL_IDEATION,
};

const q13SuicidalIdeation: DrugEliminationNode = {
  id: N_Q13_SUICIDAL_IDEATION,
  type: "drug_elimination",
  text:
    "Q13 — Does the patient have active suicidal ideation, recent self-harm, or a recent suicide attempt?",
  hint:
    "Active suicidal ideation requires urgent assessment, not a weight management consultation. Do not initiate ANY weight management medicine until mental health is stabilised. Mysimba is absolutely contraindicated (suicidality listed in SmPC). All medicines under this service are eliminated — the pathway terminates with an urgent mental health referral. If mental health stabilises, re-enter the pathway.",
  eliminateOnYes: [
    {
      id: "Q13-active-suicidal-ideation-excludes-mysimba",
      drugIds: ["mysimba"],
      reason:
        "YES at Q13: active suicidal ideation — mysimba absolutely contraindicated (suicidality listed in SmPC). Do not initiate any weight management medicine until mental health is stabilised. Refer urgently.",
    },
    {
      id: "Q13-active-suicidal-ideation-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason:
        "YES at Q13: active suicidal ideation — do not initiate any weight management medicine until mental health is stabilised. Refer urgently to GP / mental health services. Re-enter pathway if mental health stabilises.",
    },
    {
      id: "Q13-active-suicidal-ideation-excludes-semaglutide",
      drugIds: ["semaglutide", "oral_semaglutide", "foundayo"],
      reason:
        "YES at Q13: active suicidal ideation — do not initiate any weight management medicine until mental health is stabilised. Refer urgently to GP / mental health services. Re-enter pathway if mental health stabilises.",
    },
    {
      id: "Q13-active-suicidal-ideation-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason:
        "YES at Q13: active suicidal ideation — do not initiate any weight management medicine until mental health is stabilised. Refer urgently to GP / mental health services. Re-enter pathway if mental health stabilises.",
    },
    {
      id: "Q13-active-suicidal-ideation-excludes-orlistat",
      drugIds: ["orlistat"],
      reason:
        "YES at Q13: active suicidal ideation — do not initiate any weight management medicine until mental health is stabilised. Refer urgently to GP / mental health services. Re-enter pathway if mental health stabilises.",
    },
  ],
  eliminateOnNo: [],
  answerRouting: {
    yes: { outcomeId: O_REFER_MENTAL_HEALTH_URGENT },
    no: N_Q14_SEVERE_GI,
  },
  next: N_Q14_SEVERE_GI, // Unreachable: both answerRouting branches are explicit and take precedence.
  noDrugsRemaining: N_Q14_SEVERE_GI, // Unreachable: both answerRouting branches are explicit and take precedence.
};

// ─────────────────────────────────────────────────────────────────────────────
// Group C — GI and malabsorption (Q14, Q16)
// Q14 eliminates tirzepatide, semaglutide, liraglutide.
// Q16 eliminates orlistat.
// Entry point: N_Q14_SEVERE_GI (preserved — upstream routing unchanged)
// Q15 (pregnancy) is untouched; reachable via "none selected" route.
// ─────────────────────────────────────────────────────────────────────────────

const q14GroupCChecklist: MultiSelectNode = {
  id: N_Q14_SEVERE_GI,
  type: "multi_select",
  text: "Q14, 16 — Does the patient have any of the following? Select all that apply.",
  hint: "Severe GI disease contraindicates GLP-1 agents. Cholestasis and malabsorption syndromes contraindicate orlistat.",
  items: [
    {
      id: "q14_severe_gi",
      text: "Severe gastrointestinal disease — e.g. inflammatory bowel disease in active flare, gastroparesis, severe GORD, or documented delayed gastric emptying",
    },
    {
      id: "q16_cholestasis",
      text: "Cholestasis or chronic malabsorption syndrome — e.g. coeliac disease, short bowel syndrome, chronic pancreatic insufficiency",
    },
  ],
  noneOption: { label: "None of the above" },
  storesTo: "q14to16GroupCSelections",
  storesMany: {
    q14SevereGi: (ids: string[]) => ids.includes("q14_severe_gi"),
    q16Cholestasis: (ids: string[]) => ids.includes("q16_cholestasis"),
  },
  routing: (selections) => {
    if (selections.includes("q14_severe_gi")) return N_Q14_GROUP_C_ROUTE;
    if (selections.includes("q16_cholestasis")) return N_Q16_CHOLESTASIS;
    return N_Q15_SEX_GATE;
  },
};

const q14GroupCRoute: ConditionRouteNode = {
  id: N_Q14_GROUP_C_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ((ctx.context["q14to16GroupCSelections"] as string[]) ?? []).includes("q14_severe_gi"),
      then: N_Q14_GI_ELIM,
    },
    {
      evaluate: (ctx) => ((ctx.context["q14to16GroupCSelections"] as string[]) ?? []).includes("q16_cholestasis"),
      then: N_Q16_CHOLESTASIS,
    },
  ],
  default: N_Q15_SEX_GATE,
};

const q14GiElim: DrugEliminationNode = {
  id: N_Q14_GI_ELIM,
  type: "drug_elimination",
  text: "Q14 — Severe GI disease: GLP-1 agents excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q14-severe-GI-gastroparesis-excludes-tirzepatide", drugIds: ["tirzepatide"], reason: "YES at Q14: severe GI disease or gastroparesis — eliminate tirzepatide, semaglutide, liraglutide.", trigger: { kind: "always" } },
    { id: "Q14-severe-GI-gastroparesis-excludes-semaglutide", drugIds: ["semaglutide", "oral_semaglutide"], reason: "YES at Q14: severe GI disease or gastroparesis — eliminate tirzepatide, semaglutide, liraglutide.", trigger: { kind: "always" } },
    { id: "Q14-severe-GI-gastroparesis-excludes-liraglutide", drugIds: ["liraglutide"], reason: "YES at Q14: severe GI disease or gastroparesis — eliminate tirzepatide, semaglutide, liraglutide.", trigger: { kind: "always" } },
    { id: "Q14-severe-GI-excludes-foundayo", drugIds: ["foundayo"], reason: "YES at Q14: severe or unstable gastrointestinal disease, including severe gastroparesis — excluded under the Foundayo PGD.", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  next: N_Q14_GI_TO_Q16_ROUTE,
  noDrugsRemaining: N_Q14_GI_TO_Q16_ROUTE,
};

// Routes from Q14 (GI) to Q16 (cholestasis) only when cholestasis was also
// selected at the Group C checklist; otherwise bypasses Q16 entirely so
// orlistat is not eliminated without cholestasis being selected.
const q14GiToQ16Route: ConditionRouteNode = {
  id: N_Q14_GI_TO_Q16_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.context.q16Cholestasis === true,
      then: N_Q16_CHOLESTASIS,
    },
  ],
  default: N_Q15_SEX_GATE,
};

// Q15 is not asked when the confirmed biological sex is male; unknown sex still asks it.
const q15SexGate: ConditionRouteNode = {
  id: N_Q15_SEX_GATE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => confirmedBiologicalSex(ctx.patient, ctx.context) === "male",
      then: N_Q17_CICLOSPORIN,
    },
  ],
  default: N_Q15_PREGNANCY,
};

const q15Pregnancy: DrugEliminationNode = {
  id: N_Q15_PREGNANCY,
  type: "drug_elimination",
  text:
    "Q15 — Is the patient pregnant, breastfeeding, or actively planning pregnancy?",
  hint:
    "All medicines under this PGD are contraindicated in pregnancy and breastfeeding. Foundayo must be discontinued at least 3 weeks before a planned pregnancy. The pathway terminates with referral.",
  eliminateOnYes: [
    {
      id: "Q15-pregnancy-breastfeeding-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason:
        "YES at Q15: pregnancy, breastfeeding, or planned pregnancy — all medicines under this service excluded.",
    },
    {
      id: "Q15-pregnancy-breastfeeding-excludes-semaglutide",
      drugIds: ["semaglutide", "oral_semaglutide"],
      reason:
        "YES at Q15: pregnancy, breastfeeding, or planned pregnancy — all medicines under this service excluded.",
    },
    {
      id: "Q15-pregnancy-breastfeeding-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason:
        "YES at Q15: pregnancy, breastfeeding, or planned pregnancy — all medicines under this service excluded.",
    },
    {
      id: "Q15-pregnancy-breastfeeding-excludes-mysimba",
      drugIds: ["mysimba"],
      reason:
        "YES at Q15: pregnancy, breastfeeding, or planned pregnancy — all medicines under this service excluded.",
    },
    {
      id: "Q15-pregnancy-breastfeeding-excludes-orlistat",
      drugIds: ["orlistat"],
      reason:
        "YES at Q15: pregnancy, breastfeeding, or planned pregnancy — all medicines under this service excluded.",
    },
    {
      id: "Q15-pregnancy-breastfeeding-excludes-foundayo",
      drugIds: ["foundayo"],
      reason:
        "YES at Q15: pregnancy, breastfeeding, or planned pregnancy — excluded under the Foundayo PGD (discontinue at least 3 weeks before a planned pregnancy).",
    },
  ],
  eliminateOnNo: [],
  answerRouting: {
    yes: { outcomeId: O_REFER_PREGNANCY },
    no: N_Q17_CICLOSPORIN,
  },
  next: N_Q17_CICLOSPORIN, // Unreachable: both answerRouting branches are explicit and take precedence.
  noDrugsRemaining: N_Q17_CICLOSPORIN, // Unreachable: both answerRouting branches are explicit and take precedence.
};

// Q16 — autoAdvance elim. Only reachable when cholestasis was selected at the
// Group C checklist (via q14GroupCRoute or q14GiToQ16Route), so the
// elimination trigger below is unconditional ("always").
const q16CholestasisElim: DrugEliminationNode = {
  id: N_Q16_CHOLESTASIS,
  type: "drug_elimination",
  text: "Q16 — Cholestasis/malabsorption: orlistat excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q16-cholestasis-malabsorption-excludes-orlistat",
      drugIds: ["orlistat"],
      reason: "YES at Q16: cholestasis or chronic malabsorption — eliminate orlistat.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q15_SEX_GATE,
  noDrugsRemaining: N_Q15_SEX_GATE,
};

// ─────────────────────────────────────────────────────────────────────────────
// Group D — Cardiac, retinal, drug interactions (Q17, Q18, Q19)
// Q17 eliminates orlistat. Q18 eliminates tirzepatide, semaglutide.
// Q19 eliminates liraglutide.
// Entry point: N_Q17_CICLOSPORIN (preserved — upstream routing unchanged)
// ─────────────────────────────────────────────────────────────────────────────

const q17GroupDChecklist: MultiSelectNode = {
  id: N_Q17_CICLOSPORIN,
  type: "multi_select",
  text: "Q17–19 — Does the patient have any of the following? Select all that apply.",
  hint: "These conditions or medications affect specific drugs in the eligible set.",
  items: [
    {
      id: "q17_ciclosporin",
      text: "Currently taking ciclosporin — orlistat inhibits ciclosporin absorption, increasing the risk of transplant rejection or toxicity",
    },
    {
      id: "q18_retinopathy_any",
      text: "Diabetic retinopathy (any stage) or macular oedema",
    },
    {
      id: "q19_nyha_iv",
      text: "NYHA Class IV (severe) heart failure — liraglutide and Foundayo contraindicated due to insufficient safety data (and fluid balance concerns for liraglutide)",
    },
  ],
  noneOption: { label: "None of the above" },
  storesTo: "q17to19GroupDSelections",
  storesMany: {
    q17Ciclosporin: (ids: string[]) => ids.includes("q17_ciclosporin"),
    q18RetinopathyAny: (ids: string[]) => ids.includes("q18_retinopathy_any"),
    q19NyhaIV:      (ids: string[]) => ids.includes("q19_nyha_iv"),
  },
  routing: (selections) => {
    if (selections.includes("q17_ciclosporin")) return N_Q17_GROUP_D_ROUTE;
    if (selections.includes("q18_retinopathy_any")) return N_Q18_RETINOPATHY_ELIM;
    if (selections.includes("q19_nyha_iv")) return N_Q19_NYHA_ELIM;
    return N_STAGE2_EXIT_GUARD;
  },
};

const q17GroupDRoute: ConditionRouteNode = {
  id: N_Q17_GROUP_D_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ((ctx.context["q17to19GroupDSelections"] as string[]) ?? []).includes("q17_ciclosporin"),
      then: N_Q17_CICLOSPORIN_ELIM,
    },
    {
      evaluate: (ctx) => ((ctx.context["q17to19GroupDSelections"] as string[]) ?? []).includes("q18_retinopathy_any"),
      then: N_Q18_RETINOPATHY_ELIM,
    },
    {
      evaluate: (ctx) => ((ctx.context["q17to19GroupDSelections"] as string[]) ?? []).includes("q19_nyha_iv"),
      then: N_Q19_NYHA_ELIM,
    },
  ],
  default: N_STAGE2_EXIT_GUARD,
};

const q17ToQ18Route: ConditionRouteNode = {
  id: N_Q17_TO_Q18_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.context.q18RetinopathyAny === true,
      then: N_Q18_RETINOPATHY_ELIM,
    },
  ],
  default: N_Q17_TO_Q19_ROUTE,
};

const q17ToQ19Route: ConditionRouteNode = {
  id: N_Q17_TO_Q19_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.context.q19NyhaIV === true,
      then: N_Q19_NYHA_ELIM,
    },
  ],
  default: N_STAGE2_EXIT_GUARD,
};

const q18ToQ19Route: ConditionRouteNode = {
  id: N_Q18_TO_Q19_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.context.q19NyhaIV === true,
      then: N_Q19_NYHA_ELIM,
    },
  ],
  default: N_STAGE2_EXIT_GUARD,
};

const q17CiclosporinElim: DrugEliminationNode = {
  id: N_Q17_CICLOSPORIN_ELIM,
  type: "drug_elimination",
  text: "Q17 — Ciclosporin: orlistat excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    { id: "Q17-ciclosporin-excludes-orlistat", drugIds: ["orlistat"], reason: "YES at Q17: ciclosporin present — eliminate orlistat (absorption interaction, transplant rejection risk).", trigger: { kind: "always" } },
  ],
  eliminateOnNo: [],
  // Foundayo stays eligible with ciclosporin (OATP1B inhibitor) but is capped at 9 mg once daily.
  auditFlags: { yes: "q17_foundayo_ciclosporin_dose_cap_9mg" },
  next: N_Q17_TO_Q18_ROUTE,
  noDrugsRemaining: N_Q17_TO_Q18_ROUTE,
};

const q18RetinopathyElim: DrugEliminationNode = {
  id: N_Q18_RETINOPATHY_ELIM,
  type: "drug_elimination",
  text: "Q18 — Diabetic retinopathy (any stage) or macular oedema: tirzepatide and both semaglutide products excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q18-diabetic-retinopathy-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason: "Diabetic retinopathy or macular oedema — excluded per Mounjaro® PGD.",
      trigger: { kind: "always" },
    },
    {
      id: "Q18-diabetic-retinopathy-excludes-semaglutide",
      drugIds: ["semaglutide"],
      reason: "Diabetic retinopathy or macular oedema — excluded per Wegovy® PGD.",
      trigger: { kind: "always" },
    },
    {
      id: "Q18-diabetic-retinopathy-excludes-oral-semaglutide",
      drugIds: ["oral_semaglutide"],
      reason: "Diabetic retinopathy or macular oedema — excluded per Wegovy® tablets PGD.",
      trigger: { kind: "always" },
    },
    {
      id: "Q18-diabetic-retinopathy-excludes-foundayo",
      drugIds: ["foundayo"],
      reason: "Diabetic retinopathy or macular oedema — excluded under the Foundayo PGD.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q18_TO_Q19_ROUTE,
  noDrugsRemaining: N_Q18_TO_Q19_ROUTE,
};

const q19NyhaElim: DrugEliminationNode = {
  id: N_Q19_NYHA_ELIM,
  type: "drug_elimination",
  text: "Q19 — NYHA Class IV heart failure: liraglutide excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q19-NYHA-IV-heart-failure-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason: "YES at Q19: NYHA Class IV heart failure — liraglutide excluded.",
      trigger: { kind: "always" },
    },
    {
      id: "Q19-NYHA-IV-heart-failure-excludes-foundayo",
      drugIds: ["foundayo"],
      reason: "YES at Q19: NYHA Class IV heart failure — Foundayo excluded (no experience in these patients; not recommended).",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_STAGE2_EXIT_GUARD,
  noDrugsRemaining: N_STAGE2_EXIT_GUARD,
};

const stage2ExitGuard: ConditionRouteNode = {
  id: N_STAGE2_EXIT_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.length === 0,
      then: { outcomeId: O_REFER_GP },
    },
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.length > 0,
      then: N_Q20A_CYP2B6_INHIBITORS,
    },
  ],
  default: { outcomeId: O_REFER_GP }, // Defensive fallback; empty/non-empty eligible-set branches above are exhaustive.
};

// -----------------------------------------------------------------------------
// Stage 3 — Interaction screening (Q20)
// Revised per AHT_Weight_Drug_Selection_Pathway_REVISED_tracked.docx
// Single combined checklist replaces five sequential yes/no nodes.
// Tamoxifen added as new interaction (Mysimba CYP2D6 inhibition).
// Management plan capture nodes preserved — triggered by route after checklist.
// -----------------------------------------------------------------------------

const q20ManagementPlanAcknowledgement =
  "I have counselled the patient on this interaction and recorded the agreed management plan.";

// A plan step is shown only for each interaction actually ticked, in this fixed order. The first ticked
// interaction is entered via the management route; each plan then continues to the next ticked one.
const Q20_PLAN_ORDER: { optionId: string; nodeId: string }[] = [
  { optionId: "cyp2b6_inhibitors", nodeId: N_Q20_CYP2B6_INHIBITOR_PLAN },
  { optionId: "cyp2b6_inducers", nodeId: N_Q20_CYP2B6_INDUCER_PLAN },
  { optionId: "levothyroxine", nodeId: N_Q20_LEVOTHYROXINE_PLAN },
  { optionId: "warfarin_anticoagulants", nodeId: N_Q20_WARFARIN_PLAN },
  { optionId: "antiepileptic_drugs", nodeId: N_Q20_ANTIEPILEPTICS_PLAN },
  { optionId: "tamoxifen", nodeId: N_Q20_TAMOXIFEN_PLAN },
];
const q20RouterId = (i: number): string => `Q20_route_after_${Q20_PLAN_ORDER[i]!.optionId}`;
const q20NextAfter = (i: number): string =>
  i === Q20_PLAN_ORDER.length - 1 ? N_Q20F_GUARD : q20RouterId(i);
const q20Routers: ConditionRouteNode[] = Q20_PLAN_ORDER.slice(0, -1).map((_, i): ConditionRouteNode => ({
  id: q20RouterId(i),
  type: "condition_route",
  conditions: Q20_PLAN_ORDER.slice(i + 1).map((o) => ({
    evaluate: (ctx) =>
      ((ctx.context["q20InteractionSelections"] as string[] | undefined) ?? []).includes(o.optionId),
    then: o.nodeId,
  })),
  default: N_Q20F_GUARD,
}));

// Q20 — Combined interactions checklist
// Node ID reuses N_Q20A_CYP2B6_INHIBITORS so stage2ExitGuard routing is unchanged.
const q20InteractionsChecklist: MultiSelectNode = {
  id: N_Q20A_CYP2B6_INHIBITORS,
  type: "multi_select",
  text:
    "Q20 — Is the patient currently taking any of the following? Select all that apply.",
  hint:
    "Check all that apply. For each selected interaction, a management plan will be captured. If any critical interaction cannot be safely managed, exclude the affected drug. If all can be managed with monitoring, document the plan and proceed.",
  items: [
    {
      id: "warfarin_anticoagulants",
      text:
        "Warfarin or other oral anticoagulants — all medicines under this service can interact; Orlistat may increase anticoagulant effect (INR monitoring required); GLP-1 agents/tirzepatide may affect warfarin absorption via delayed gastric emptying",
    },
    {
      id: "antiepileptic_drugs",
      text:
        "Antiepileptic drugs (valproate, lamotrigine, carbamazepine, phenytoin) — Orlistat reduces AED absorption; Mysimba: carbamazepine/phenytoin are CYP2B6 inducers (may reduce bupropion exposure)",
    },
    {
      id: "levothyroxine",
      text:
        "Levothyroxine — Orlistat reduces levothyroxine absorption; separate administration times and monitor TFTs",
    },
    {
      id: "tamoxifen",
      text:
        "Tamoxifen — Mysimba: bupropion inhibits CYP2D6 and may reduce tamoxifen metabolic activation and efficacy; consider an alternative weight loss medicine",
    },
    {
      id: "narrow_ti_oral_medicines",
      text:
        "Narrow therapeutic index oral medicines (e.g. digoxin, lithium, ciclosporin, certain antiarrhythmics) — GLP-1 agents/tirzepatide: delayed gastric emptying may affect absorption; monitor clinical response and drug levels",
    },
    {
      id: "cyp2b6_inhibitors",
      text:
        "Strong CYP2B6 inhibitors (e.g. ticlopidine, clopidogrel) — increase Mysimba (bupropion) exposure; slower dose escalation and closer monitoring required",
    },
    {
      id: "cyp2b6_inducers",
      text:
        "CYP2B6 inducers (e.g. ritonavir, efavirenz, carbamazepine, phenobarbital) — decrease Mysimba (bupropion) exposure; may reduce efficacy",
    },
  ],
  noneOption: { label: "None of the above — no relevant interactions identified" },
  storesTo: "q20InteractionSelections",
  routing: (selections) =>
    selections.length > 0
      ? N_Q20_MANAGEMENT_ROUTE
      : N_Q20F_GUARD,
};

// Q20 management route — routes to relevant management plan nodes based on selections
const q20ManagementRoute: ConditionRouteNode = {
  id: N_Q20_MANAGEMENT_ROUTE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => {
        const selections = (ctx.context["q20InteractionSelections"] as string[] | undefined) ?? [];
        return selections.includes("cyp2b6_inhibitors");
      },
      then: N_Q20_CYP2B6_INHIBITOR_PLAN,
    },
    {
      evaluate: (ctx) => {
        const selections = (ctx.context["q20InteractionSelections"] as string[] | undefined) ?? [];
        return selections.includes("cyp2b6_inducers");
      },
      then: N_Q20_CYP2B6_INDUCER_PLAN,
    },
    {
      evaluate: (ctx) => {
        const selections = (ctx.context["q20InteractionSelections"] as string[] | undefined) ?? [];
        return selections.includes("levothyroxine");
      },
      then: N_Q20_LEVOTHYROXINE_PLAN,
    },
    {
      evaluate: (ctx) => {
        const selections = (ctx.context["q20InteractionSelections"] as string[] | undefined) ?? [];
        return selections.includes("warfarin_anticoagulants");
      },
      then: N_Q20_WARFARIN_PLAN,
    },
    {
      evaluate: (ctx) => {
        const selections = (ctx.context["q20InteractionSelections"] as string[] | undefined) ?? [];
        return selections.includes("antiepileptic_drugs");
      },
      then: N_Q20_ANTIEPILEPTICS_PLAN,
    },
    {
      evaluate: (ctx) => {
        const selections = (ctx.context["q20InteractionSelections"] as string[] | undefined) ?? [];
        return selections.includes("tamoxifen");
      },
      then: N_Q20_TAMOXIFEN_PLAN,
    },
  ],
  default: N_Q20F_GUARD,
};

// Q20b — CYP2B6 inhibitor management plan (Mysimba)
// Retained from original Q20a — now only reached if cyp2b6_inhibitors selected
const q20aCyp2b6Inhibitors: DrugEliminationNode = {
  id: N_Q20_CYP2B6_INHIBITOR_PLAN,
  type: "drug_elimination",
  text:
    "Q20 — CYP2B6 inhibitor interaction with Mysimba: document the management plan.",
  hint:
    "Strong CYP2B6 inhibitors increase bupropion (Mysimba component) exposure. Document: dose-escalation pace, monitoring plan (BP, insomnia, headache), and any agreed review timing.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q20_mysimba_cyp2b6_inhibitor_interaction",
  },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document the management plan for the CYP2B6 inhibitor interaction with Mysimba (bupropion). Include: dose-escalation pace, monitoring plan (BP, insomnia, headache), and any agreed review timing.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20_mysimba_cyp2b6_inhibitor_management_plan",
    displayLabel: "Mysimba and CYP2B6 inhibitor interaction management plan",
  },
  next: q20NextAfter(0),
  noDrugsRemaining: q20NextAfter(0),
};

// Q20c — CYP2B6 inducer management plan (Mysimba)
const q20bCyp2b6Inducers: DrugEliminationNode = {
  id: N_Q20_CYP2B6_INDUCER_PLAN,
  type: "drug_elimination",
  text:
    "Q20 — CYP2B6 inducer interaction with Mysimba: document the management plan.",
  hint:
    "CYP2B6 inducers decrease bupropion (Mysimba component) exposure and may reduce efficacy. Document: whether to proceed with Mysimba or consider an alternative, monitoring plan, and review timing.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q20_mysimba_cyp2b6_inducer_interaction",
  },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document the management plan for the CYP2B6 inducer interaction with Mysimba (bupropion). Include: whether to proceed with Mysimba or consider an alternative agent, monitoring plan, and review timing.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20_mysimba_cyp2b6_inducer_management_plan",
    displayLabel: "Mysimba and CYP2B6 inducer interaction management plan",
  },
  next: q20NextAfter(1),
  noDrugsRemaining: q20NextAfter(1),
};

// Q20d — Levothyroxine management plan (Orlistat)
const q20cLevothyroxine: DrugEliminationNode = {
  id: N_Q20_LEVOTHYROXINE_PLAN,
  type: "drug_elimination",
  text:
    "Q20 — Levothyroxine interaction with Orlistat: document the management plan.",
  hint:
    "Orlistat reduces levothyroxine absorption. Document: dose-separation timing (levothyroxine first thing in morning, orlistat with main meals), TFT monitoring schedule, and GP review.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q20_orlistat_levothyroxine_interaction",
  },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document the management plan for the levothyroxine interaction with Orlistat. Include: dose-separation timing, TFT monitoring schedule, and any agreed GP review.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20_orlistat_levothyroxine_management_plan",
    displayLabel: "Orlistat and levothyroxine interaction management plan",
  },
  next: q20NextAfter(2),
  noDrugsRemaining: q20NextAfter(2),
};

// Q20e — Warfarin / anticoagulant management plan (all drugs)
const q20dWarfarin: DrugEliminationNode = {
  id: N_Q20_WARFARIN_PLAN,
  type: "drug_elimination",
  text:
    "Q20 — Warfarin / anticoagulant interaction: document the management plan.",
  hint:
    "All medicines under this service can interact with oral anticoagulants. Orlistat may increase anticoagulant effect — INR monitoring required. GLP-1 agents/tirzepatide: delayed gastric emptying may affect warfarin absorption — monitor INR after initiation. Document plan for affected drug(s).",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q20_anticoagulant_interaction",
  },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document the management plan for the warfarin/anticoagulant interaction. Include: increased INR monitoring frequency, communication with anticoagulation clinic/GP, and any agreed review timing.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20_anticoagulant_management_plan",
    displayLabel: "Anticoagulant interaction management plan",
  },
  next: q20NextAfter(3),
  noDrugsRemaining: q20NextAfter(3),
};

// Q20f — Antiepileptic drug management plan (Orlistat / Mysimba) — NEW
const q20eAntiepileptics: DrugEliminationNode = {
  id: N_Q20_ANTIEPILEPTICS_PLAN,
  type: "drug_elimination",
  text:
    "Q20 — Antiepileptic drug interaction: document the management plan.",
  hint:
    "Orlistat may reduce AED absorption — monitor seizure frequency, consider dose-timing separation and specialist liaison. Mysimba: carbamazepine/phenytoin are CYP2B6 inducers — may reduce bupropion exposure. Seek GP/neurologist input if Mysimba is being considered.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q20_antiepileptic_interaction",
  },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document the management plan for the antiepileptic drug interaction. Include: dose-timing separation for Orlistat, seizure-diary review, specialist liaison plan, and CYP2B6 impact on Mysimba if applicable.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20_antiepileptic_management_plan",
    displayLabel: "Antiepileptic drug interaction management plan",
  },
  next: q20NextAfter(4),
  noDrugsRemaining: q20NextAfter(4),
};

// Q20g — Tamoxifen management plan (Mysimba) — NEW
// Source: revised pathway — bupropion inhibits CYP2D6; may reduce tamoxifen efficacy
const q20fTamoxifen: DrugEliminationNode = {
  id: N_Q20_TAMOXIFEN_PLAN,
  type: "drug_elimination",
  text:
    "Q20 — Tamoxifen interaction with Mysimba: document the management plan or exclude Mysimba.",
  hint:
    "Bupropion (Mysimba component) inhibits CYP2D6 and may reduce tamoxifen metabolic activation and efficacy. The revised AHT pathway recommends considering an alternative weight loss medicine if the patient is on tamoxifen. If Mysimba is to proceed, document rationale and inform the oncology team.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: {
    yes: "q20_tamoxifen_mysimba_interaction",
  },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document the management plan for the tamoxifen/Mysimba interaction. Include: whether to proceed with Mysimba or select an alternative agent, oncology team notification, and any agreed monitoring.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20_tamoxifen_management_plan",
    displayLabel: "Mysimba and tamoxifen interaction management plan",
  },
  next: N_Q20F_GUARD,
  noDrugsRemaining: N_Q20F_GUARD,
};

const stage3ExitGuard: ConditionRouteNode = {
  id: N_STAGE3_EXIT_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.length === 0,
      then: { outcomeId: O_REFER_GP },
    },
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.length > 0,
      then: N_QP1_GUARD,
    },
  ],
  default: { outcomeId: O_REFER_GP },
};

// -----------------------------------------------------------------------------
// Stage 4 — Patient preferences (QP1–QP3)
// -----------------------------------------------------------------------------

const qp1Guard: ConditionRouteNode = {
  id: N_QP1_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => hasAnyInjectable(ctx.eligibleDrugIds),
      then: N_QP1_INJECTION_WILLING,
    },
    {
      evaluate: (ctx) => !hasAnyInjectable(ctx.eligibleDrugIds),
      then: N_QP2_GUARD,
    },
  ],
  default: N_QP2_GUARD,
};

const qp1InjectionWilling: DrugEliminationNode = {
  id: N_QP1_INJECTION_WILLING,
  type: "drug_elimination",
  text: "QP1 — Is the patient willing to self-administer injectable medication?",
  answerPolarity: "neutral",
  hint:
    "Tirzepatide, semaglutide, and liraglutide are all self-injected (weekly for tirzepatide and semaglutide, daily for liraglutide). If the patient is unwilling to inject, the choice narrows to the oral options that are still eligible, which the eligibility panel shows. Discuss needle technique and storage requirements before answering — willingness can sometimes change with practical demonstration.",
  eliminateOnYes: [],
  eliminateOnNo: [
    {
      id: "QP1-not-willing-to-inject-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason:
        "NO at QP1: patient unwilling to inject — eliminate tirzepatide, semaglutide, and liraglutide. Only the oral options remain, subject to eligibility.",
    },
    {
      id: "QP1-not-willing-to-inject-excludes-semaglutide",
      drugIds: ["semaglutide"],
      reason:
        "NO at QP1: patient unwilling to inject — eliminate tirzepatide, semaglutide, and liraglutide. Only the oral options remain, subject to eligibility.",
    },
    {
      id: "QP1-not-willing-to-inject-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason:
        "NO at QP1: patient unwilling to inject — eliminate tirzepatide, semaglutide, and liraglutide. Only the oral options remain, subject to eligibility.",
    },
  ],
  next: N_QP2_GUARD,
  noDrugsRemaining: N_QP2_GUARD,
  capturePreference: true,
};

const qp2Guard: ConditionRouteNode = {
  id: N_QP2_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) =>
        hasWeeklyInjectable(ctx.eligibleDrugIds) &&
        hasDailyInjectable(ctx.eligibleDrugIds),
      then: N_QP2_WEEKLY_VS_DAILY,
    },
    {
      evaluate: (ctx) =>
        !(
          hasWeeklyInjectable(ctx.eligibleDrugIds) &&
          hasDailyInjectable(ctx.eligibleDrugIds)
        ),
      then: N_QP3_GUARD,
    },
  ],
  default: N_QP3_GUARD,
};

const qp2WeeklyVsDaily: SingleSelectNode = {
  id: N_QP2_WEEKLY_VS_DAILY,
  type: "single_select",
  text: "QP2 — Does the patient have a preference for injection frequency?",
  hint:
    "Tirzepatide and semaglutide are once-weekly injections. Liraglutide is a once-daily injection. Some patients prefer the convenience of weekly dosing; others prefer the routine of a daily dose. If no strong preference, select 'No strong preference' and the clinical ranking will choose.",
  options: [
    { id: "weekly", label: "Prefers once-weekly injection" },
    { id: "daily", label: "Prefers once-daily injection" },
    { id: "no_preference", label: "No strong preference" },
  ],
  storesTo: "qp2InjectionFrequencyPreference",
  required: true,
  routing: {
    weekly: N_QP2_WEEKLY_ELIM,
    daily: N_QP2_DAILY_ELIM,
    no_preference: N_QP3_GUARD,
  },
  capturePreference: true,
};

const qp2WeeklyElim: DrugEliminationNode = {
  id: N_QP2_WEEKLY_ELIM,
  type: "drug_elimination",
  text:
    "Injection frequency preference recorded. The eligible set has been updated to reflect the patient's choice.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "QP2-weekly-preference-excludes-liraglutide",
      drugIds: ["liraglutide"],
      reason: "QP2 weekly injection preference: liraglutide eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_QP3_GUARD,
  noDrugsRemaining: N_QP3_GUARD,
};

const qp2DailyElim: DrugEliminationNode = {
  id: N_QP2_DAILY_ELIM,
  type: "drug_elimination",
  text:
    "Injection frequency preference recorded. The eligible set has been updated to reflect the patient's choice.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "QP2-daily-preference-excludes-tirzepatide",
      drugIds: ["tirzepatide"],
      reason: "QP2 daily injection preference: tirzepatide and semaglutide eliminated.",
      trigger: { kind: "always" },
    },
    {
      id: "QP2-daily-preference-excludes-semaglutide",
      drugIds: ["semaglutide"],
      reason: "QP2 daily injection preference: tirzepatide and semaglutide eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_QP3_GUARD,
  noDrugsRemaining: N_QP3_GUARD,
};

const qp3Guard: ConditionRouteNode = {
  id: N_QP3_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => hasBothOrals(ctx.eligibleDrugIds),
      then: N_QP3_ORAL_PREFERENCE,
    },
    {
      evaluate: (ctx) => !hasBothOrals(ctx.eligibleDrugIds),
      then: N_STAGE4_EXIT_GUARD,
    },
  ],
  default: N_STAGE4_EXIT_GUARD,
};

const qp3OralPreference: MultiSelectNode = {
  id: N_QP3_ORAL_PREFERENCE,
  type: "multi_select",
  text:
    "QP3 — Which factors are influencing the choice between Mysimba and orlistat? Select all that apply.",
  hint:
    "Mysimba and orlistat both remain eligible. Capture any factors that the patient discusses or that you identify clinically as favouring one over the other. If factors balance or no factors are identified, the clinical ranking will choose. Selections are recorded in audit.",
  noneOption: { label: "None of the above — no specific factors influencing choice" },
  groups: [
    {
      id: "favours_orlistat",
      label: "Factors favouring orlistat over Mysimba",
      items: [
        {
          id: "history_depression_mood_disorder",
          text: "History of depression or mood disorder",
        },
        {
          id: "avoid_cns_active_medication",
          text: "Patient prefers to avoid CNS-active medication",
        },
        {
          id: "concern_insomnia_sleep",
          text: "Concern about insomnia or sleep effects",
        },
        {
          id: "prefer_established_efficacy_data",
          text: "Patient prefers established efficacy data",
        },
      ],
    },
    {
      id: "favours_mysimba",
      label: "Factors favouring Mysimba over orlistat",
      items: [
        {
          id: "concern_gi_side_effects",
          text: "Concerns about GI side effects (steatorrhoea, urgency, oily stools)",
        },
        {
          id: "low_fat_diet_difficulty",
          text: "Difficulty adhering to a low-fat diet",
        },
        {
          id: "concern_vitamin_deficiency",
          text: "Concern about fat-soluble vitamin deficiency",
        },
        {
          id: "concurrent_dietary_supplements_malabsorption_risk",
          text: "Concurrent dietary supplements at risk of malabsorption",
        },
      ],
    },
  ],
  storesTo: "qp3OralPreferenceFactors",
  routing: (selections) => {
    const favOrl = hasFavoursOrlistatFactor(selections);
    const favMys = hasFavoursMysimbaFactor(selections);
    if (favOrl && !favMys) return N_QP3_ELIM_ORLISTAT;
    if (favMys && !favOrl) return N_QP3_ELIM_MYSIMBA;
    return N_STAGE4_EXIT_GUARD;
  },
  capturePreference: true,
};

const qp3OrlistatElim: DrugEliminationNode = {
  id: N_QP3_ELIM_ORLISTAT,
  type: "drug_elimination",
  text:
    "Oral preference factors recorded. Factors favour orlistat, so Mysimba has been eliminated from the eligible set.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "QP3-favours-orlistat-only-excludes-mysimba",
      drugIds: ["mysimba"],
      reason: "QP3: factors favour orlistat — mysimba eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_STAGE4_EXIT_GUARD,
  noDrugsRemaining: N_STAGE4_EXIT_GUARD,
};

const qp3MysimbaElim: DrugEliminationNode = {
  id: N_QP3_ELIM_MYSIMBA,
  type: "drug_elimination",
  text:
    "Oral preference factors recorded. Factors favour Mysimba, so orlistat has been eliminated from the eligible set.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "QP3-favours-mysimba-only-excludes-orlistat",
      drugIds: ["orlistat"],
      reason: "QP3: factors favour Mysimba — orlistat eliminated.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_STAGE4_EXIT_GUARD,
  noDrugsRemaining: N_STAGE4_EXIT_GUARD,
};

const stage4ExitGuard: ConditionRouteNode = {
  id: N_STAGE4_EXIT_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.length === 0,
      then: { outcomeId: O_REFER_GP },
    },
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.length > 0,
      then: N_COMORBIDITY_MODIFIER_NAFLD_GATE,
    },
  ],
  default: { outcomeId: O_REFER_GP },
};

// ─────────────────────────────────────────────────────────────────────────────
// Stage 4b — Comorbidity modifiers
// Source: AHT_Weight_Drug_Selection_Pathway_REVISED_tracked.docx — Stage 4
// Clinical preference modifiers applied after patient preferences (Stage 4)
// and before drug resolution (Stage 5).
//
// NAFLD/Mysimba: hard sign-off gate (same pattern as Q9).
// All other modifiers: audit flag annotations only — Option B approach.
// Pharmacist override remains available at drug_resolution.
// ─────────────────────────────────────────────────────────────────────────────

// Gate: does NAFLD apply AND is Mysimba still eligible?
const comorbidityModifierNafldGate: ConditionRouteNode = {
  id: N_COMORBIDITY_MODIFIER_NAFLD_GATE,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => {
        const comorbidities =
          (ctx.context["q2aComorbiditySelections"] as string[] | undefined) ?? [];
        const mysimbaEligible = ctx.eligibleDrugIds.includes("mysimba");
        return comorbidities.includes("nafld") && mysimbaEligible;
      },
      then: N_COMORBIDITY_MODIFIER_NAFLD_UPLOAD,
    },
    {
      evaluate: (ctx) => {
        const comorbidities =
          (ctx.context["q2aComorbiditySelections"] as string[] | undefined) ?? [];
        const mysimbaEligible = ctx.eligibleDrugIds.includes("mysimba");
        return !comorbidities.includes("nafld") || !mysimbaEligible;
      },
      then: N_COMORBIDITY_MODIFIER_ANNOTATIONS,
    },
  ],
  default: N_COMORBIDITY_MODIFIER_ANNOTATIONS,
};

// NAFLD/Mysimba — document upload instruction
const comorbidityModifierNafldUpload: GatewayProgressionNode = {
  id: N_COMORBIDITY_MODIFIER_NAFLD_UPLOAD,
  type: "gateway_progression",
  heading: "NAFLD/NASH present — Mysimba requires responsible-clinician sign-off",
  body: "Mysimba (bupropion/naltrexone) lists drug-induced liver injury as a recognised adverse drug reaction. In patients with NAFLD/NASH, Mysimba must NOT be selected without:\n\n1. Responsible-clinician sign-off confirming Mysimba is appropriate\n2. Baseline LFTs on file\n\nIf you have these, upload them now using the Add Action button in the Actions panel \u2192 Document Upload.\n\nIf you do not have responsible-clinician sign-off and baseline LFTs, do not select Mysimba \u2014 proceed and choose an alternative eligible medicine at drug resolution.\n\nTap Continue when ready.",
  triggersPayment: false,
  next: N_COMORBIDITY_MODIFIER_NAFLD_ATTEST,
};

// NAFLD/Mysimba — attestation
const comorbidityModifierNafldAttestation: AttestationNode = {
  id: N_COMORBIDITY_MODIFIER_NAFLD_ATTEST,
  type: "attestation",
  text: "NAFLD/NASH — Mysimba sign-off confirmation",
  attestationStatement:
    "I confirm one of the following regarding Mysimba and this patient's NAFLD/NASH diagnosis:",
  conclusions: {
    optionA: {
      label:
        "Responsible-clinician sign-off and baseline LFTs have been obtained and uploaded \u2014 Mysimba may be considered at drug resolution",
      next: N_COMORBIDITY_MODIFIER_ANNOTATIONS,
    },
    optionB: {
      label:
        "Sign-off or baseline LFTs not available \u2014 I will not select Mysimba at drug resolution",
      next: N_COMORBIDITY_MODIFIER_ANNOTATIONS,
    },
  },
};

// Comorbidity ranking annotation node
// Comorbidity modifier annotation chain.
// Each condition gate checks one comorbidity from q2aComorbiditySelections.
// When selected, routes through an autoAdvance DrugEliminationNode that
// registers a bare-string auditFlag into audit.flagsSet (read by displayAccumulatedFlags).
// Source: AHT revised pathway Stage 4 clinical preference modifiers.

// Gate 1 — CVD
const comorbidityModifierAnnotations: ConditionRouteNode = {
  id: N_COMORBIDITY_MODIFIER_ANNOTATIONS,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => {
        const comorbidities =
          (ctx.context["q2aComorbiditySelections"] as string[] | undefined) ?? [];
        return comorbidities.includes("cardiovascular_disease");
      },
      then: N_COMORBIDITY_CVD_FLAG,
    },
  ],
  default: N_COMORBIDITY_NAFLD_ANNOT,
};

const comorbidityModifierCvdFlag: DrugEliminationNode = {
  id: N_COMORBIDITY_CVD_FLAG,
  type: "drug_elimination",
  text: "CVD comorbidity modifier",
  autoAdvance: true,
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: "CVD_PREFER_SEMAGLUTIDE",
  next: N_COMORBIDITY_NAFLD_ANNOT,
  noDrugsRemaining: N_COMORBIDITY_NAFLD_ANNOT,
};

// Gate 2 — NAFLD
const comorbidityModifierNafldAnnotCheck: ConditionRouteNode = {
  id: N_COMORBIDITY_NAFLD_ANNOT,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => {
        const comorbidities =
          (ctx.context["q2aComorbiditySelections"] as string[] | undefined) ?? [];
        return comorbidities.includes("nafld");
      },
      then: N_COMORBIDITY_NAFLD_FLAG,
    },
  ],
  default: N_COMORBIDITY_PCOS_ANNOT,
};

const comorbidityModifierNafldFlag: DrugEliminationNode = {
  id: N_COMORBIDITY_NAFLD_FLAG,
  type: "drug_elimination",
  text: "NAFLD comorbidity modifier",
  autoAdvance: true,
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: "NAFLD_PREFER_SEMAGLUTIDE",
  next: N_COMORBIDITY_PCOS_ANNOT,
  noDrugsRemaining: N_COMORBIDITY_PCOS_ANNOT,
};

// Gate 3 — PCOS
const comorbidityModifierPcosAnnotCheck: ConditionRouteNode = {
  id: N_COMORBIDITY_PCOS_ANNOT,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => {
        const comorbidities =
          (ctx.context["q2aComorbiditySelections"] as string[] | undefined) ?? [];
        return comorbidities.includes("pcos");
      },
      then: N_COMORBIDITY_PCOS_FLAG,
    },
  ],
  default: N_COMORBIDITY_OA_ANNOT,
};

const comorbidityModifierPcosFlag: DrugEliminationNode = {
  id: N_COMORBIDITY_PCOS_FLAG,
  type: "drug_elimination",
  text: "PCOS comorbidity modifier",
  autoAdvance: true,
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: "PCOS_MILD_PREFERENCE_SEMAGLUTIDE",
  next: N_COMORBIDITY_OA_ANNOT,
  noDrugsRemaining: N_COMORBIDITY_OA_ANNOT,
};

// Gate 4 — Osteoarthritis
const comorbidityModifierOaAnnotCheck: ConditionRouteNode = {
  id: N_COMORBIDITY_OA_ANNOT,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => {
        const comorbidities =
          (ctx.context["q2aComorbiditySelections"] as string[] | undefined) ?? [];
        return comorbidities.includes("osteoarthritis");
      },
      then: N_COMORBIDITY_OA_FLAG,
    },
  ],
  default: N_DRUG_RESOLUTION,
};

const comorbidityModifierOaFlag: DrugEliminationNode = {
  id: N_COMORBIDITY_OA_FLAG,
  type: "drug_elimination",
  text: "Osteoarthritis comorbidity modifier",
  autoAdvance: true,
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: "OSTEOARTHRITIS_MILD_PREFERENCE_SEMAGLUTIDE",
  next: N_DRUG_RESOLUTION,
  noDrugsRemaining: N_DRUG_RESOLUTION,
};

const drugResolution: DrugResolutionNode = {
  id: N_DRUG_RESOLUTION,
  type: "drug_resolution",
  // SIGN-OFF REQUIRED: OI-F2 — Foundayo's rank is a default (after oral semaglutide); the sources set no position.
  rankingOrder: ["tirzepatide", "semaglutide", "oral_semaglutide", "foundayo", "liraglutide", "mysimba", "orlistat"],
  singleDrugNext: N_RESOLUTION_CONFIRMATION,
  multipleDrugsNext: N_RESOLUTION_CONFIRMATION,
  noDrugsRemaining: { outcomeId: O_REFER_GP },
  displayAccumulatedFlags: true,
  displayAccumulatedManagementPlans: true,
  displayAccumulatedPreferences: true,
  pharmacistOverrideAllowed: true,
  rowCallouts: (ctx) => {
    const out: Record<string, string[]> = {};
    const mysimba: string[] = [];
    if (ctx.context["q6SulphonylureaInsulin"] === "yes") {
      mysimba.push("Concurrent sulphonylurea or insulin: Mysimba may affect glycaemic control — flag for GP review.");
    }
    const q7 = (ctx.context["q7RenalHepatic"] as string[] | undefined) ?? [];
    if (q7.includes("moderate_renal")) {
      mysimba.push("Moderate renal impairment (CrCl 30–59 mL/min): Mysimba dose reduction required — maximum 2 tablets/day.");
    }
    if (mysimba.length > 0) out["mysimba"] = mysimba;
    return out;
  },
};

const resolutionConfirmation: AttestationNode = {
  id: N_RESOLUTION_CONFIRMATION,
  type: "attestation",
  text: "Confirm recommended medicine",
  attestationStatement:
    "I confirm that I have reviewed the recommended medicine, all captured caution flags, and all management plans documented in this consultation. The recommended medicine is appropriate for this patient under the relevant PGD.",
  notesPrompt:
    "Optional — add any supplementary notes for the consultation record (e.g. counselling points discussed, patient questions, follow-up plan).",
  conclusions: {
    optionA: {
      label: "Confirm and proceed to supply",
      next: { outcomeId: O_SUPPLY_SELECTED },
    },
    optionB: {
      label: "Cannot confirm — refer to GP",
      next: { outcomeId: O_REFER_GP },
    },
  },
};

// -----------------------------------------------------------------------------
// Foundayo-only screens (PGD v2.1). They never change the other drugs' eligibility.
// -----------------------------------------------------------------------------

// Q9c — disordered eating features. Only asked while Foundayo is still eligible.
const q9cFoundayoGuard: ConditionRouteNode = {
  id: N_Q9C_FDY_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.includes("foundayo"),
      then: N_Q9C_FDY_SCREEN,
    },
  ],
  default: N_Q12_UNCONTROLLED_BP,
};

// PGD v2.1 (Warnings, disordered eating): ask directly about each feature. Each is listed separately and the
// ticked set is stored. "None of these" is an explicit answer; "unsure" excludes (fail safe).
const Q9C_UNSURE_OPTION = "fdy_ed_unsure";
const Q9C_FEATURE_OPTIONS = [
  { id: "fdy_ed_binge_eating", text: "Binge eating" },
  { id: "fdy_ed_self_induced_vomiting", text: "Self-induced vomiting" },
  { id: "fdy_ed_laxative_diuretic_misuse", text: "Laxative or diuretic use for weight control" },
  { id: "fdy_ed_compulsive_exercise", text: "Compulsive exercise" },
  { id: "fdy_ed_weight_shape_preoccupation", text: "Preoccupation with weight or shape that you assess as disproportionate" },
  { id: Q9C_UNSURE_OPTION, text: "Patient is unsure, or an answer could not be obtained" },
] as const;

const q9cFoundayoScreen: MultiSelectNode = {
  id: N_Q9C_FDY_SCREEN,
  type: "multi_select",
  text:
    "Q9c — Foundayo only: ask the patient directly. Does the patient currently have any of the following? Select all that apply.",
  hint:
    "Orforglipron suppresses appetite and may mask or worsen an eating disorder. Ask about each feature directly. If any is present, or the patient is unsure, Foundayo is excluded: refer to the GP with the patient's consent and signpost to eating disorder support. Do not simply decline and close the consultation. If there is an immediate risk to health, refer for same day medical assessment.",
  items: Q9C_FEATURE_OPTIONS.map((o) => ({ id: o.id, text: o.text })),
  noneOption: { label: "None of these" },
  storesTo: "q9cFoundayoDisorderedEating",
  routing: (selections) => (selections.length > 0 ? N_Q9C_FDY_ELIM : N_Q12_UNCONTROLLED_BP),
};

const q9cFoundayoElim: DrugEliminationNode = {
  id: N_Q9C_FDY_ELIM,
  type: "drug_elimination",
  text: "Q9c — Features suggestive of disordered eating: Foundayo excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q9c-disordered-eating-features-excludes-foundayo",
      drugIds: ["foundayo"],
      reason:
        "Q9c: current features suggestive of disordered eating, or the patient is unsure — excluded under the Foundayo PGD. Refer to the GP with consent and signpost to eating disorder support.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_Q12_UNCONTROLLED_BP,
  noDrugsRemaining: N_Q12_UNCONTROLLED_BP,
};

// Q20F — Foundayo medicines check. Runs after the shared Q20, only while Foundayo is still eligible.
// Each ticked plan item is shown once, in this fixed order; the router after each plan continues to the next ticked one.
const Q20F_PLAN_ORDER: { optionId: string; nodeId: string }[] = [
  { optionId: "fdy_oral_topotecan", nodeId: N_Q20F_TOPOTECAN_PLAN },
  { optionId: "fdy_statin", nodeId: N_Q20F_STATIN_PLAN },
  { optionId: "fdy_dose_cap", nodeId: N_Q20F_CAP_PLAN },
  { optionId: "fdy_moderate_inducer", nodeId: N_Q20F_INDUCER_PLAN },
];
const Q20F_EXCLUDE_OPTION = "fdy_excluded_medicines";
const q20fRouterId = (i: number): string => `Q20F_route_after_${Q20F_PLAN_ORDER[i]!.optionId}`;
const q20fNextAfter = (i: number): string =>
  i === Q20F_PLAN_ORDER.length - 1 ? N_STAGE3_EXIT_GUARD : q20fRouterId(i);
const q20fFirstTicked = (selections: string[]): string =>
  Q20F_PLAN_ORDER.find((o) => selections.includes(o.optionId))?.nodeId ?? N_STAGE3_EXIT_GUARD;
const q20fRouters: ConditionRouteNode[] = Q20F_PLAN_ORDER.slice(0, -1).map((_, i): ConditionRouteNode => ({
  id: q20fRouterId(i),
  type: "condition_route",
  conditions: Q20F_PLAN_ORDER.slice(i + 1).map((o) => ({
    evaluate: (ctx) =>
      ((ctx.context["q20fFoundayoSelections"] as string[] | undefined) ?? []).includes(o.optionId),
    then: o.nodeId,
  })),
  default: N_STAGE3_EXIT_GUARD,
}));

const q20fGuard: ConditionRouteNode = {
  id: N_Q20F_GUARD,
  type: "condition_route",
  conditions: [
    {
      evaluate: (ctx) => ctx.eligibleDrugIds.includes("foundayo"),
      then: N_Q20F_CHECKLIST,
    },
  ],
  default: N_STAGE3_EXIT_GUARD,
};

const q20fChecklist: MultiSelectNode = {
  id: N_Q20F_CHECKLIST,
  type: "multi_select",
  text: "Q20F — Foundayo only: is the patient currently taking any of the following? Select all that apply.",
  hint:
    "These apply to orforglipron (Foundayo®) only and do not change the other medicines. Check the full medication list, including over-the-counter and herbal products such as St John's wort. The antiepileptic item in Q20 does not remove Foundayo by itself: tick the first item below for carbamazepine or phenytoin.",
  items: [
    {
      id: Q20F_EXCLUDE_OPTION,
      text:
        "Ritonavir, telaprevir, or a strong CYP3A4 inducer (rifampicin, phenytoin, carbamazepine, St John's wort) — excludes Foundayo",
    },
    {
      id: "fdy_oral_topotecan",
      text:
        "Oral topotecan — Foundayo only with written confirmation from the oncology team that supply is appropriate (recorded)",
    },
    {
      id: "fdy_statin",
      text:
        "Simvastatin, or rosuvastatin above 20 mg daily — the prescriber must be contacted and the plan documented before supply",
    },
    {
      id: "fdy_dose_cap",
      text:
        "Strong CYP3A4 inhibitor (ketoconazole, clarithromycin, itraconazole) or clinical OATP1B inhibitor (ciclosporin) — Foundayo maximum 9 mg once daily",
    },
    {
      id: "fdy_moderate_inducer",
      text:
        "Moderate CYP3A4 inducer (bosentan, efavirenz) — may reduce effectiveness; monitor response and document",
    },
  ],
  noneOption: { label: "None of the above" },
  storesTo: "q20fFoundayoSelections",
  routing: (selections) =>
    selections.includes(Q20F_EXCLUDE_OPTION) ? N_Q20F_EXCLUDE_ELIM : q20fFirstTicked(selections),
};

const q20fExcludeElim: DrugEliminationNode = {
  id: N_Q20F_EXCLUDE_ELIM,
  type: "drug_elimination",
  text: "Q20F — Ritonavir, telaprevir or a strong CYP3A4 inducer: Foundayo excluded.",
  autoAdvance: true,
  eliminateOnYes: [
    {
      id: "Q20F-excluded-medicines-exclude-foundayo",
      drugIds: ["foundayo"],
      reason:
        "Concomitant ritonavir, telaprevir or a strong CYP3A4 inducer (rifampicin, phenytoin, carbamazepine, St John's wort): excluded under the Foundayo PGD. Refer to the GP where a prescribing decision is needed.",
      trigger: { kind: "always" },
    },
  ],
  eliminateOnNo: [],
  next: N_STAGE3_EXIT_GUARD,
  noDrugsRemaining: N_STAGE3_EXIT_GUARD,
};

const q20fTopotecanPlan: DrugEliminationNode = {
  id: N_Q20F_TOPOTECAN_PLAN,
  type: "drug_elimination",
  text: "Q20F — Oral topotecan with Foundayo: record the oncology team's written confirmation.",
  hint:
    "Do not supply Foundayo unless the patient's oncology team has confirmed in writing that supply is appropriate. Record who confirmed, when, and how it was received. If there is no confirmation, do not select Foundayo.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: { yes: "q20f_foundayo_oral_topotecan" },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Record the written confirmation from the oncology team that Foundayo supply is appropriate with oral topotecan: who confirmed, the date, and how it was received. If there is no written confirmation, do not supply Foundayo.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20f_foundayo_oral_topotecan_management_plan",
    displayLabel: "Foundayo and oral topotecan: oncology confirmation",
  },
  next: q20fNextAfter(0),
  noDrugsRemaining: q20fNextAfter(0),
};

const q20fStatinPlan: DrugEliminationNode = {
  id: N_Q20F_STATIN_PLAN,
  type: "drug_elimination",
  text: "Q20F — Simvastatin or high-dose rosuvastatin with Foundayo: document the prescriber contact and plan.",
  hint:
    "Orforglipron raises simvastatin acid exposure (the SmPC requires the simvastatin dose to be halved) and rosuvastatin exposure. Halving a statin dose is a prescribing decision outside this PGD. Do not supply until the prescriber has been contacted and the plan documented, or the patient has been switched by that prescriber.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: { yes: "q20f_foundayo_statin_interaction" },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document that the prescriber of the statin has been contacted, who was contacted, the date, and the agreed dose change or monitoring plan. Do not supply until this is recorded.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20f_foundayo_statin_management_plan",
    displayLabel: "Foundayo and statin interaction management plan",
  },
  next: q20fNextAfter(1),
  noDrugsRemaining: q20fNextAfter(1),
};

const q20fCapPlan: DrugEliminationNode = {
  id: N_Q20F_CAP_PLAN,
  type: "drug_elimination",
  text: "Q20F — Strong CYP3A4 inhibitor or OATP1B inhibitor with Foundayo: maximum dose 9 mg once daily.",
  hint:
    "Foundayo stays eligible but the maximum dose is 9 mg once daily. For a short course while established above 9 mg, reduce to 9 mg for the duration of the course and resume the previous dose afterwards; record the reduction, the reason and the resume date. A dose held only because of an interacting medicine is a temporary cap, not a maximum tolerated dose.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: { yes: "q20f_foundayo_dose_cap_9mg" },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Record the interacting medicine, that the maximum Foundayo dose is capped at 9 mg once daily, and (for a short course) the date the previous dose will be resumed.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20f_foundayo_dose_cap_management_plan",
    displayLabel: "Foundayo 9 mg dose cap management plan",
  },
  next: q20fNextAfter(2),
  noDrugsRemaining: q20fNextAfter(2),
};

const q20fInducerPlan: DrugEliminationNode = {
  id: N_Q20F_INDUCER_PLAN,
  type: "drug_elimination",
  text: "Q20F — Moderate CYP3A4 inducer with Foundayo: document the monitoring plan.",
  hint:
    "Moderate CYP3A4 inducers (bosentan, efavirenz) may reduce effectiveness. Monitor response and document; escalate within the licensed range as tolerated.",
  eliminateOnYes: [],
  eliminateOnNo: [],
  auditFlags: { yes: "q20f_foundayo_moderate_inducer" },
  managementPlan: {
    triggeredByAnswer: "yes",
    planPrompt:
      "Document the monitoring plan for response to Foundayo while the patient takes a moderate CYP3A4 inducer.",
    acknowledgementText: q20ManagementPlanAcknowledgement,
    auditField: "q20f_foundayo_moderate_inducer_management_plan",
    displayLabel: "Foundayo and moderate CYP3A4 inducer monitoring plan",
  },
  next: q20fNextAfter(3),
  noDrugsRemaining: q20fNextAfter(3),
};

const stage2B2bNodes = [
  q3GroupAChecklist,
  q3GroupARoute,
  q3ThyroidElim,
  q4PancreatitisElim,
  q5T1dmElim,
  q6SulphonylureasInsulin,
];

const stage2B2cNodes = [
  q7RenalHepatic,
  ...q7Routers,
  q7SevereRenalElim,
  q7LiraglutideRenalElim,
  q7OralSemaEgfrElim,
  q7ModerateRenalFlag,
  q7SevereHepaticElim,
  q7ModerateHepaticElim,
  q8GroupBChecklist,
  q8GroupBRoute,
  q8SeizuresElim,
  q10OpioidElim,
  q11MaoisElim,
  q9aEatingDisorder,
  q9bBipolar,
  q9EatingDisorderUpload,
  q9EatingDisorderAttestation,
  q9BipolarUpload,
  q9BipolarAttestation,
  q9cFoundayoGuard,
  q9cFoundayoScreen,
  q9cFoundayoElim,
];

const stage2B2dNodes = [
  q12BpInstruction,
  q12BpDecision,
  q12BpMysimbaElim,
  q13SuicidalIdeation,
  q14GroupCChecklist,
  q14GroupCRoute,
  q14GiElim,
  q14GiToQ16Route,
  q15SexGate,
  q15Pregnancy,
];

const stage2B2eNodes = [
  q16CholestasisElim,
  q17GroupDChecklist,
  q17GroupDRoute,
  q17ToQ18Route,
  q17ToQ19Route,
  q18ToQ19Route,
  q17CiclosporinElim,
  q18RetinopathyElim,
  q19NyhaElim,
  stage2ExitGuard,
];

// -----------------------------------------------------------------------------
// Pathway outcomes
// -----------------------------------------------------------------------------

const pathwayOutcomes: Pathway["outcomes"] = {
  [O_REFER_UNDER12]: {
    id: O_REFER_UNDER12,
    type: "outcome",
    outcomeType: "refer_routine",
    title: "Refer — patient under 12",
    reason:
      "No weight management medicine under this PGD is licensed or covered for patients under 12 years. Refer to paediatric specialist / GP.",
    referralOptions: ["Paediatric specialist / GP"],
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_REFER_OVER84]: {
    id: O_REFER_OVER84,
    type: "outcome",
    outcomeType: "refer_routine",
    title: "Refer — patient aged 85 or over",
    reason:
      "All medicines under this service are limited to ages 18–84 (or 18–74 for liraglutide and mysimba). Refer to GP for specialist weight management options.",
    referralOptions: ["GP"],
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_REFER_GP]: {
    id: O_REFER_GP,
    type: "outcome",
    outcomeType: "refer_routine",
    title: "Refer to GP — no medicine eligible under this PGD",
    reason:
      "Document all excluded drugs and reasons · Refer to GP with summary — consider referral to Tier 3 specialist weight management service · Offer lifestyle support: diet, physical activity, and behavioural intervention.",
    referralOptions: [],
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_REFER_BMI_BELOW27]: {
    id: O_REFER_BMI_BELOW27,
    type: "outcome",
    outcomeType: "refer_routine",
    title: "Refer to GP — BMI below 27 kg/m²",
    reason:
      "BMI is below 27 kg/m² and one of the following applies: (a) the patient is not currently established on a weight management medicine under this PGD; (b) the patient is established on tirzepatide and BMI is below 22 (PGD discontinuation threshold); or (c) the patient is established on semaglutide and BMI is below 22.5 (PGD discontinuation threshold). Document the captured drug (if any), the BMI value, and refer to GP for review. Offer lifestyle support: diet, physical activity, and behavioural intervention.",
    referralOptions: ["GP"],
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_REFER_ADOLESCENT_FAILED]: {
    id: O_REFER_ADOLESCENT_FAILED,
    type: "outcome",
    outcomeType: "refer_routine",
    title: "Refer — adolescent eligibility criteria not met",
    reason:
      "One or more of the adolescent eligibility criteria for liraglutide (weight, IOTF cut-off, competence/capacity, eating disorder screen, safeguarding, or face-to-face requirement) was not met. Document the criterion not met and refer to GP / CAMHS as appropriate.",
    referralOptions: ["GP / CAMHS"],
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_REFER_MENTAL_HEALTH_URGENT]: {
    id: O_REFER_MENTAL_HEALTH_URGENT,
    type: "outcome",
    outcomeType: "refer_urgent",
    title: "Urgent — refer for mental health assessment",
    reason:
      "Active suicidal ideation, recent self-harm, or recent suicide attempt requires urgent mental health assessment, not a weight management consultation. Do not supply any medicine under this PGD today. If the patient is in immediate danger, follow local urgent referral and safeguarding procedures (e.g. crisis team, A&E, or emergency services). Document the assessment and the referral pathway used.",
    referralOptions: ["GP / mental health crisis team / A&E as clinically indicated"],
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_REFER_PREGNANCY]: {
    id: O_REFER_PREGNANCY,
    type: "outcome",
    outcomeType: "refer_routine",
    title: "Refer — pregnancy, breastfeeding, or planned pregnancy",
    reason:
      "All medicines under this service are contraindicated in pregnancy and breastfeeding. Refer to GP. Provide lifestyle support and discuss contraception requirements: adequate contraception for at least 2 months after stopping tirzepatide or semaglutide, and at least 1 month after stopping liraglutide.",
    referralOptions: ["Antenatal care / GP"],
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_BLOCKED_PENDING_IOTF]: {
    id: O_BLOCKED_PENDING_IOTF,
    type: "outcome",
    outcomeType: "cannot_supply",
    title: "Cannot proceed — IOTF cut-off data not available",
    reason:
      "The IOTF age- and sex-specific obesity cut-off cannot be resolved for this patient (data pending). Pause this consultation and resume once the IOTF reference value is available.",
    safetyNetting: [], // TODO: AHT clinical sign-off required.
  },
  [O_SUPPLY_SELECTED]: {
    id: O_SUPPLY_SELECTED,
    type: "outcome",
    outcomeType: "chain",
    chainId: "aht-wt-initial-supply-chain",
    title: "Proceed to initial supply",
    reason:
      "The recommended medicine has been confirmed. Proceeding to the drug-specific initial supply pathway.",
    safetyNetting: [],
  },
};

// -----------------------------------------------------------------------------
// Pathway node lists
// -----------------------------------------------------------------------------

const stage1Nodes = [
  foundayoNeverRestartElim,
  transferLockRoute,
  transferLockElim,
  q1AgeRoute,
  q1Age75Elim,
  // Q1a adolescent branch
  q1aWeightPresentRoute,
  q1aWeightEntry,
  q1aWeightGate,
  q1aIotfLookup,
  q1aConsent,
  q1aEatingDisorder,
  q1aSafeguarding,
  q1aParental,
  q1aFaceToFace,
  q1aAdolescentElim,
  // Q2 BMI
  q2BmiPresentRoute,
  q2BmiEntry,
  q2BmiBelow27Route,
  q2OrlistatThresholdElim,
  // Q2 BMI <27 established continuation sub-flow
  q2ReinitFlag,
  q2ReinitLockRoute,
  q2EstablishedCheck,
  q2EstablishedDrugPick,
  q2EstablishedFloorRoute,
  q2ContOsem,
  q2ContTirz,
  q2ContSema,
  q2ContLira,
  q2ContMysi,
  q2ContOrli,
  q2ContFoundayo,
  // Q2a comorbidities
  q2aComorbidities,
  q2aLiraglutideQualifyingElim,
  q2aT2dmRoute,
  q2aNoComorbidityRoute,
  q2aNoComorbidityEthnicElim,
  // Q2b HbA1c
  q2bHba1cEntry,
  q2bHba1cRoute,
  q2bHba1cMidFlag,
  q2bHba1cHighElim,
  q2bHba1cHighOrlistatCheck,
  q2bHba1cRecency,
  q2bHba1cStaleElim,
];

const stage2Nodes = [
  ...stage2B2bNodes,
  ...stage2B2cNodes,
  ...stage2B2dNodes,
  ...stage2B2eNodes,
];

const stage3Nodes = [
  q20InteractionsChecklist,
  q20ManagementRoute,
  ...q20Routers,
  q20aCyp2b6Inhibitors,
  q20bCyp2b6Inducers,
  q20cLevothyroxine,
  q20dWarfarin,
  q20eAntiepileptics,
  q20fTamoxifen,
  q20fGuard,
  q20fChecklist,
  q20fExcludeElim,
  ...q20fRouters,
  q20fTopotecanPlan,
  q20fStatinPlan,
  q20fCapPlan,
  q20fInducerPlan,
  stage3ExitGuard,
];

const stage4Nodes = [
  qp1Guard,
  qp1InjectionWilling,
  qp2Guard,
  qp2WeeklyVsDaily,
  qp2WeeklyElim,
  qp2DailyElim,
  qp3Guard,
  qp3OralPreference,
  qp3OrlistatElim,
  qp3MysimbaElim,
  stage4ExitGuard,
  comorbidityModifierNafldGate,
  comorbidityModifierNafldUpload,
  comorbidityModifierNafldAttestation,
  comorbidityModifierAnnotations,
  comorbidityModifierCvdFlag,
  comorbidityModifierNafldAnnotCheck,
  comorbidityModifierNafldFlag,
  comorbidityModifierPcosAnnotCheck,
  comorbidityModifierPcosFlag,
  comorbidityModifierOaAnnotCheck,
  comorbidityModifierOaFlag,
];

const stage5Nodes = [drugResolution, resolutionConfirmation];

const pathwayNodes = [...stage1Nodes, ...stage2Nodes, ...stage3Nodes, ...stage4Nodes, ...stage5Nodes];

// TODO: AHT clinical sign-off required for these working Stage 5 review labels.
const auditFlagLabels: NonNullable<Pathway["auditFlagLabels"]> = {
  parental_involvement_not_documented:
    "Adolescent consultation: parent/guardian involvement not documented",
  bmi_below_27_established_continuation_attested:
    "BMI below 27: established treatment continuation attested",
  bmi_below_27_returning_restart_criteria_met:
    "BMI below 27: returning patient, restart criteria met in the shared assessment",
  bmi_below_27_established_continuation_resolved:
    "BMI below 27: continuation restricted to the established medicine",
  q2b_hba1c_mid_band_gp_review_required:
    "HbA1c 58–85 mmol/mol: GP review of diabetes treatment recommended",
  q2b_hba1c_high_specialist_referral_required:
    "HbA1c ≥86 mmol/mol: specialist diabetes review required; GLP-1/GIP and Mysimba excluded",
  q6_mysimba_hypoglycaemia_caution:
    "Mysimba: hypoglycaemia caution with sulphonylurea or insulin treatment",
  q9_liraglutide_adult_eating_disorder_bipolar_caution:
    "Liraglutide: adult eating disorder or bipolar disorder caution",
  q14_mysimba_gi_caution:
    "Mysimba: severe GI disease/gastroparesis caution",
  q14_orlistat_gi_caution:
    "Orlistat: severe GI disease/gastroparesis caution",
  q20a_mysimba_cyp2b6_inhibitor_interaction:
    "Mysimba: CYP2B6 inhibitor interaction (dose escalation and monitoring required)",
  q20b_mysimba_cyp2b6_inducer_interaction:
    "Mysimba: CYP2B6 inducer interaction (efficacy and monitoring plan required)",
  q20c_orlistat_levothyroxine_interaction:
    "Orlistat: levothyroxine interaction (dose separation and TFT monitoring required)",
  q20d_orlistat_warfarin_interaction:
    "Orlistat: warfarin interaction (increased INR monitoring required)",
  q20e_orlistat_antiepileptic_interaction:
    "Orlistat: antiepileptic interaction (seizure-control monitoring and specialist liaison required)",
  q2a_foundayo_off_label_ethnic_band:
    "Foundayo: BMI 27.5–29.9 without a weight-related comorbidity (ethnic adjustment band) is off label — informed consent to off label supply must be recorded.",
  q17_foundayo_ciclosporin_dose_cap_9mg:
    "Foundayo: ciclosporin (OATP1B inhibitor) — maximum dose 9 mg once daily if Foundayo is selected.",
  q20f_foundayo_oral_topotecan:
    "Foundayo: oral topotecan — written oncology confirmation required before supply.",
  q20f_foundayo_statin_interaction:
    "Foundayo: simvastatin or rosuvastatin above 20 mg — prescriber contact and plan required before supply.",
  q20f_foundayo_dose_cap_9mg:
    "Foundayo: strong CYP3A4 or OATP1B inhibitor — maximum dose 9 mg once daily if Foundayo is selected.",
  q20f_foundayo_moderate_inducer:
    "Foundayo: moderate CYP3A4 inducer — monitor response and document.",
  q7_mysimba_dose_reduction_flag:
    "Mysimba dose reduction required: moderate renal impairment (CrCl 30–59 mL/min) — maximum 2 tablets/day. Flag for dose adjustment.",
  CVD_PREFER_SEMAGLUTIDE:
    "CVD present — clinical preference: semaglutide (Wegovy\u00ae SELECT trial cardiovascular outcomes evidence). Tirzepatide does not hold this licensed indication. Consider at drug resolution.",
  NAFLD_PREFER_SEMAGLUTIDE:
    "NAFLD/NASH present — clinical preference: semaglutide. Mysimba: drug-induced liver injury listed as ADR — sign-off confirmed above.",
  PCOS_MILD_PREFERENCE_SEMAGLUTIDE:
    "PCOS present — mild clinical preference: semaglutide (Wegovy\u00ae PGD names PCOS as qualifying comorbidity).",
  OSTEOARTHRITIS_MILD_PREFERENCE_SEMAGLUTIDE:
    "Osteoarthritis present — mild clinical preference: semaglutide (Wegovy\u00ae PGD names osteoarthritis as qualifying comorbidity).",
};

// -----------------------------------------------------------------------------
// Pathway shell — Stages 1–5 assembled.
// -----------------------------------------------------------------------------

export const ahtWeightSelectionPathway: Pathway = {
  id: "aht-weight-selection",
  name: "AHT Weight Management",
  population:
    "AHT weight management patients being assessed for medicine selection under the tirzepatide, semaglutide, liraglutide, Mysimba, orlistat PGDs", // TODO: AHT confirmation required.
  pgdRefs: [
    "pgd-aht-wt-tirzepatide",
    "pgd-aht-wt-semaglutide",
    "pgd-aht-wt-liraglutide",
    "pgd-aht-wt-mysimba",
    "pgd-aht-wt-orlistat",
    "pgd-aht-wt-foundayo",
  ],
  inputTypeId: "aht_weight",
  demographicConfiguration: { bannerFields: [] },
  initialEligibleDrugIds: [
    "tirzepatide",
    "semaglutide",
    "oral_semaglutide",
    "foundayo",
    "liraglutide",
    "mysimba",
    "orlistat",
  ],
  entryNode: N_FDY_NEVER_RESTART,
  nodes: pathwayNodes,
  outcomes: pathwayOutcomes,
  auditFlagLabels,
};
